import React, { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Maximize2, Minimize2 } from 'lucide-react'
import { api } from '../lib/api'
import { asArray, asCollection } from '../lib/collections'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { useToast } from '../ui/ToastProvider'
import { formatMenuInstant } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import { PageFrame, PageHeader } from '../ui/PageLayout'

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

const emptyEducationLessonForm = () => ({
  title: '',
  description: '',
  youtubeUrl: '',
  materialName: '',
  materialUrl: '',
  publicFlag: true,
  sortOrder: '',
})

const educationVisibilityPlanOptions = [
  { value: 'ALL', label: '전체' },
  { value: 'BASIC', label: 'BASIC' },
  { value: 'ENTERPRISE', label: 'ENTERPRISE' },
]

function educationVisibilityPlanLabel(value) {
  return educationVisibilityPlanOptions.find((option) => option.value === value)?.label || '전체'
}

const emptyEducationClassForm = () => ({
  scope: 'ORGANIZATION',
  orgId: '',
  title: '',
  description: '',
  intro: '',
  notice: '',
  sharedOrgIds: [],
  visibilityPlanCode: 'ALL',
  required: false,
  status: 'DRAFT',
})

const emptyEducationClassCreateForm = () => ({
  title: '',
  description: '',
  intro: '',
  notice: '',
  sharedOrgIds: [],
  visibilityPlanCode: 'ALL',
  required: false,
  status: 'DRAFT',
})

const educationPlanSelectClassName = 'mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none'

const educationVisibilityPlanSelect = (value, onChange, className = educationPlanSelectClassName) => (
  <select value={value || 'ALL'} onChange={(e) => onChange(e.target.value)} className={className}>
    {educationVisibilityPlanOptions.map((option) => (
      <option key={option.value} value={option.value}>{option.label}</option>
    ))}
  </select>
)

const educationButtonClass = {
  commit: 'rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90 disabled:bg-slate-300 disabled:opacity-60',
  create: 'rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-600 disabled:opacity-60',
  createSmall: 'rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-600 disabled:opacity-60',
  createSoft: 'rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-60',
  secondary: 'rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50',
  outline: 'rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60',
  danger: 'rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50',
}

const educationSelectedClass = {
  tab: 'border border-sky-200 bg-sky-50 text-brand-navy',
  pill: 'border border-sky-200 bg-sky-50 text-brand-navy',
  item: 'border-brand-sky/30 bg-sky-50 text-brand-navy',
  row: 'bg-sky-50 text-brand-navy',
}

function roleCanManage(hasAnyRole) {
  return hasAnyRole([
    Roles.OPERATOR,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
    Roles.GA_LEADER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_MANAGER,
    Roles.GA_OPERATION_MANAGER,
    Roles.GA_AFFAIRS,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_OTHER_ADMIN,
    Roles.INSURER_MASTER_ADMIN,
    Roles.INSURER_MASTER_DEPUTY_ADMIN,
    Roles.INSURER_MANAGER,
    Roles.INSURER_OPERATION_MANAGER,
    Roles.INSURER_LEADER,
    Roles.INSURER_HEAD_MANAGER,
    Roles.INSURER_DEPUTY_LEADER,
    Roles.INSURER_BRANCH_MANAGER,
    Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    Roles.INSURER_EDUCATION_MANAGER,
    Roles.INSURER_OTHER_ADMIN,
  ])
}

function formatDate(value) {
  if (!value) return '상시'
  const text = formatMenuInstant({ createdAt: value }, 'createdAt')
  return text === '-' ? String(value) : text.slice(0, 10)
}

function periodLabel(item) {
  if (!item?.startsAt && !item?.endsAt) return '상시 수강'
  return `${formatMenuInstant(item, 'startsAt')} ~ ${formatMenuInstant(item, 'endsAt')}`
}

function categoryName(item) {
  return item?.title || item?.category || '공통'
}

/** 수강 카탈로그 카드/상단: 버킷(클래스 없이 등록된 강의)은 상위 카테고리명만, 일반 클래스는 클래스명 */
function catalogClassDisplayTitle(item) {
  if (!item) return '공통'
  if (item.lessonBucket) return item.educationCategoryTitle || item.title || '강의'
  return categoryName(item)
}

function statusLabel(status) {
  const labels = { DRAFT: '비공개', PUBLISHED: '공개', ARCHIVED: '보관' }
  return labels[status] || status || ''
}

function enrollmentLabel(status) {
  const labels = {
    NOT_ENROLLED: '신청 전',
    ENROLLED: '신청완료',
    IN_PROGRESS: '수강중',
    COMPLETED: '완료',
    EXPIRED: '기간만료',
    CANCELLED: '취소',
  }
  return labels[status] || '신청 전'
}

function lessonStatusLabel(lesson) {
  if (lesson?.completed || lesson?.lessonStatus === 'COMPLETED') return '수강완료'
  if (lesson?.lastViewedAt || lesson?.lessonStatus === 'IN_PROGRESS') return '수강중'
  return '미수강'
}

function educationWatermarkBase(user) {
  const name = String(user?.name || user?.email || '사용자').trim()
  const org = String(user?.orgName || user?.activeMembership?.orgName || '조직').trim()
  return `${name} · ${org}`
}

function formatWatermarkTime(value) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(value.getMinutes())}`
}

function secureEducationEmbedUrl(raw) {
  const value = String(raw || '').trim()
  if (!value) return ''
  try {
    const url = new URL(value, window.location.origin)
    const host = url.hostname.toLowerCase()
    if (host.includes('youtu.be') || host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
      let videoId = ''
      const segments = url.pathname.split('/').filter(Boolean)
      const embedIdx = segments.indexOf('embed')
      if (embedIdx >= 0 && segments[embedIdx + 1]) videoId = segments[embedIdx + 1]
      if (!videoId && host.includes('youtu.be') && segments[0]) videoId = segments[0]
      if (!videoId && url.searchParams.get('v')) videoId = url.searchParams.get('v')
      if (!videoId && segments[0] === 'shorts' && segments[1]) videoId = segments[1]
      if (videoId) {
        const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
        embed.searchParams.set('rel', '0')
        embed.searchParams.set('playsinline', '1')
        embed.searchParams.set('modestbranding', '1')
        embed.searchParams.set('fs', '0')
        return embed.toString()
      }
      url.searchParams.set('rel', '0')
      url.searchParams.set('playsinline', '1')
      url.searchParams.set('modestbranding', '1')
      url.searchParams.set('fs', '0')
      return url.toString()
    }
  } catch {
    return value
  }
  return value
}

function EducationVideoWatermark({ base }) {
  const [now, setNow] = useState(() => new Date())
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000)
    return () => window.clearInterval(timer)
  }, [])
  const text = `${base} · ${formatWatermarkTime(now)}`
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden" aria-hidden="true">
      <style>{`
        @keyframes education-watermark-drift {
          0% { left: 6%; top: 12%; }
          25% { left: 42%; top: 30%; }
          50% { left: 18%; top: 62%; }
          75% { left: 34%; top: 48%; }
          100% { left: 6%; top: 12%; }
        }
      `}</style>
      <div
        className="absolute max-w-[54%] rounded bg-black/35 px-3 py-1.5 text-[11px] font-semibold leading-tight text-white shadow"
        style={{ animation: 'education-watermark-drift 36s linear infinite' }}
      >
        <span className="block truncate">{text}</span>
      </div>
      <div className="absolute bottom-3 right-3 max-w-[86%] rounded bg-black/30 px-2.5 py-1 text-[10px] font-semibold leading-tight text-white/85">
        <span className="block truncate">{text}</span>
      </div>
    </div>
  )
}

function EducationVideoFrame({ lesson, fallbackThumbnailUrl, watermarkBase }) {
  const frameRef = useRef(null)
  const [fullscreen, setFullscreen] = useState(false)
  const embedUrl = useMemo(() => secureEducationEmbedUrl(lesson?.embedUrl), [lesson?.embedUrl])
  React.useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      } else {
        await frameRef.current?.requestFullscreen?.()
      }
    } catch {
      setFullscreen(false)
    }
  }
  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden bg-slate-100 ${fullscreen ? 'h-screen w-screen rounded-none bg-black' : 'rounded-2xl'}`}
    >
      <div className={fullscreen ? 'h-full w-full' : 'aspect-video'}>
        {embedUrl ? (
          <iframe
            title={lesson?.title || '강의 영상'}
            src={embedUrl}
            className="h-full w-full bg-black"
            referrerPolicy="origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          />
        ) : fallbackThumbnailUrl ? (
          <img src={fallbackThumbnailUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center bg-slate-900 text-sm font-semibold text-white">교육 카테고리</div>
        )}
      </div>
      {embedUrl && <EducationVideoWatermark base={watermarkBase} />}
      {embedUrl && (
        <button
          type="button"
          onClick={toggleFullscreen}
          className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-black/45 text-white shadow hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white/80"
          aria-label={fullscreen ? '전체화면 종료' : '전체화면 보기'}
          title={fullscreen ? '전체화면 종료' : '전체화면 보기'}
        >
          {fullscreen ? <Minimize2 size={17} aria-hidden="true" /> : <Maximize2 size={17} aria-hidden="true" />}
        </button>
      )}
    </div>
  )
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)))
  return (
    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
      <div className="h-full rounded-full bg-brand-sky transition-all" style={{ width: `${pct}%` }} />
    </div>
  )
}

function LessonTeaserCard({ lesson, classItem, onOpen, onEnroll, enrolling }) {
  const enrolled = Boolean(classItem?.enrolled)
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <button type="button" onClick={onOpen} className="flex flex-1 flex-col text-left">
        <div className="relative aspect-[5/3] overflow-hidden bg-slate-100">
          {lesson.thumbnailUrl ? (
            <img src={lesson.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center bg-slate-900 text-xs font-semibold text-white">강의</div>
          )}
          <span className="absolute left-2 top-2 rounded-full border border-white/70 bg-black/45 px-2 py-0.5 text-[10px] font-bold text-white">VOD</span>
          {classItem?.required && <span className="absolute right-2 top-2 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-slate-900">필수</span>}
        </div>
        <div className="flex flex-1 flex-col p-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">강의</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${enrolled ? educationSelectedClass.pill : 'bg-slate-100 text-slate-600'}`}>
              {enrollmentLabel(classItem?.enrollmentStatus)}
            </span>
          </div>
          <h3 className="mt-2 line-clamp-2 text-sm font-bold leading-5 text-slate-900">{lesson.title || '제목 없음'}</h3>
          {lesson.description && <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-600">{lesson.description}</p>}
          <div className="mt-auto pt-3">
            {enrolled ? (
              <div>
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500">
                  <span>진도</span>
                  <span className="text-brand-navy">
                    {lesson.completed ? '완료' : lesson.lastViewedAt || lesson.lessonStatus === 'IN_PROGRESS' ? '수강중' : '미수강'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-[11px] font-semibold text-slate-500">수강 신청 후 학습할 수 있습니다.</div>
            )}
          </div>
        </div>
      </button>
      <div className="flex gap-2 border-t border-slate-100 p-3">
        {!enrolled && (
          <button type="button" onClick={onEnroll} disabled={enrolling} className={`flex-1 ${educationButtonClass.create}`}>
            {enrolling ? '신청 중...' : '수강신청'}
          </button>
        )}
        <button type="button" onClick={onOpen} className={`${enrolled ? 'flex-1 bg-slate-900 text-white hover:bg-slate-800' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'} rounded-lg px-2.5 py-1.5 text-xs font-semibold`}>
          {enrolled ? '이어보기' : '상세보기'}
        </button>
      </div>
    </article>
  )
}

function CourseCard({ item, onOpen, onEnroll, enrolling }) {
  const enrolled = Boolean(item.enrolled)
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <button type="button" onClick={onOpen} className="flex flex-1 flex-col text-left">
        <div className="relative aspect-[5/3] overflow-hidden bg-slate-100">
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center bg-slate-900 text-xs font-semibold text-white">교육 카테고리</div>
          )}
          <span className="absolute left-2 top-2 rounded-full border border-white/70 bg-black/45 px-2 py-0.5 text-[10px] font-bold text-white">VOD</span>
          {item.required && <span className="absolute right-2 top-2 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-slate-900">필수</span>}
        </div>
        <div className="flex flex-1 flex-col p-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">카테고리</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${enrolled ? educationSelectedClass.pill : 'bg-slate-100 text-slate-600'}`}>
              {enrollmentLabel(item.enrollmentStatus)}
            </span>
          </div>
          <h3 className="mt-2 line-clamp-2 text-sm font-bold leading-5 text-slate-900">{catalogClassDisplayTitle(item)}</h3>
          {item.description && <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-600">{item.description}</p>}
          <div className="mt-auto pt-3">
            {enrolled ? (
              <div>
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500">
                  <span>진도율</span>
                  <span className="text-brand-navy">{item.progressRate || 0}%</span>
                </div>
                <div className="mt-1.5"><ProgressBar value={item.progressRate} /></div>
              </div>
            ) : (
              <div className="text-[11px] font-semibold text-slate-500">{periodLabel(item)}</div>
            )}
            <div className="mt-2 flex flex-wrap justify-between gap-2 text-[11px] text-slate-500">
              <span>총 {item.totalLessons || 0}강</span>
              <span>조회 {item.viewedCount || 0}</span>
              <span>{item.scope === 'PLATFORM' ? '설계EZ' : item.orgName || '조직'}</span>
            </div>
          </div>
        </div>
      </button>
      <div className="flex gap-2 border-t border-slate-100 p-3">
        {!enrolled && (
          <button type="button" onClick={onEnroll} disabled={enrolling} className={`flex-1 ${educationButtonClass.create}`}>
            {enrolling ? '신청 중...' : '수강신청'}
          </button>
        )}
        <button type="button" onClick={onOpen} className={`${enrolled ? 'flex-1 bg-slate-900 text-white hover:bg-slate-800' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'} rounded-lg px-2.5 py-1.5 text-xs font-semibold`}>
          {enrolled ? '이어보기' : '상세보기'}
        </button>
      </div>
    </article>
  )
}

function OrgShareSelector({ orgOptions, selectedIds, onChange }) {
  const [keyword, setKeyword] = useState('')
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(Number)), [selectedIds])
  const selectedOrgs = useMemo(
    () => orgOptions.filter((org) => selectedSet.has(Number(org.id))),
    [orgOptions, selectedSet],
  )
  const candidates = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    if (!q) return []
    return orgOptions
      .filter((org) => !selectedSet.has(Number(org.id)))
      .filter((org) => `${org.name || ''} ${org.id || ''}`.toLowerCase().includes(q))
      .slice(0, 8)
  }, [keyword, orgOptions, selectedSet])

  const addOrg = (orgId) => {
    onChange([...selectedSet, Number(orgId)])
    setKeyword('')
  }
  const removeOrg = (orgId) => {
    onChange([...selectedSet].filter((id) => id !== Number(orgId)))
  }

  return (
    <div className="md:col-span-2">
      <label className="text-xs font-semibold text-slate-500">공유 조직</label>
      <input
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        placeholder="조직명을 검색해 추가"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
      />
      {candidates.length > 0 && (
        <div className="mt-2 max-h-48 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          {candidates.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => addOrg(org.id)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span className="font-semibold text-slate-800">{org.name}</span>
              <span className="text-xs text-slate-400">#{org.id}</span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedOrgs.length === 0 ? (
          <span className="text-xs text-slate-500">공유 조직이 없습니다.</span>
        ) : selectedOrgs.map((org) => (
          <span key={org.id} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {org.name}
            <button type="button" onClick={() => removeOrg(org.id)} className="text-slate-400 hover:text-slate-700">×</button>
          </span>
        ))}
      </div>
    </div>
  )
}

function LessonPlayer({
  lesson,
  commentText,
  setCommentText,
  onComplete,
  completing,
  onSaveMemo,
  savingMemo,
  onSaveComment,
  savingComment,
  activeTab,
  setActiveTab,
  commentInputRef,
}) {
  const [memo, setMemo] = useState(lesson?.memo || '')
  React.useEffect(() => setMemo(lesson?.memo || ''), [lesson?.id, lesson?.memo])

  if (!lesson) {
    return <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">강의를 선택하세요.</div>
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{lesson.title}</h3>
          {lesson.description && <p className="mt-1 text-sm leading-6 text-slate-600">{lesson.description}</p>}
        </div>
        <button type="button" onClick={onComplete} disabled={completing || lesson.completed} className={educationButtonClass.commit}>
          {lesson.completed ? '수강 완료됨' : completing ? '처리 중...' : '수강 완료'}
        </button>
      </div>
      {lesson.materialUrl && (
        <a className="mt-4 inline-flex text-sm font-semibold text-brand-sky hover:underline" href={lesson.materialUrl} target="_blank" rel="noreferrer">
          자료 다운로드: {lesson.materialName || '자료'}
        </a>
      )}
      <div className="mt-5 flex gap-2 border-b border-slate-200">
        {[
          ['notes', '수업노트'],
          ['comments', '댓글'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`border-b-2 px-3 py-2 text-sm font-semibold ${
              activeTab === id ? 'border-brand-sky text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {activeTab === 'notes' && (
        <div className="mt-4 grid gap-2">
          <label className="text-xs font-semibold text-slate-500">수업노트</label>
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={5} className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-sky" />
          <div className="flex justify-end">
            <button type="button" onClick={() => onSaveMemo(memo)} disabled={savingMemo} className={educationButtonClass.outline}>노트 저장</button>
          </div>
        </div>
      )}
      {activeTab === 'comments' && (
        <div className="mt-4">
          <textarea
            ref={commentInputRef}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={4}
            placeholder="수강 후 느낀 점을 남겨주세요."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-sky"
          />
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={onSaveComment} disabled={savingComment || !commentText.trim()} className={educationButtonClass.createSoft}>댓글 등록</button>
          </div>
          <div className="mt-4 space-y-2">
            {(lesson.comments || []).length === 0 ? (
              <div className="rounded-xl bg-slate-50 px-3 py-4 text-sm text-slate-500">아직 등록된 후기가 없습니다.</div>
            ) : lesson.comments.map((comment) => (
              <div key={comment.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="text-xs font-semibold text-slate-500">{comment.authorName || '작성자'} · {formatMenuInstant(comment, 'createdAt')}</div>
                <div className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{comment.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function EducationCatalog({ mode = 'catalog' }) {
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null)
  const [orgOnly, setOrgOnly] = useState(false)
  const [inProgressOnly, setInProgressOnly] = useState(false)
  const [requiredOnly, setRequiredOnly] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [myTab, setMyTab] = useState('continue')
  const [myStatusFilter, setMyStatusFilter] = useState('continue')
  const [lessonDetailTab, setLessonDetailTab] = useState('notes')
  const [commentText, setCommentText] = useState('')
  const commentInputRef = useRef(null)

  const homeQuery = useQuery({ queryKey: ['education-home'], queryFn: async () => unwrap(await api.get('/education/home')) })
  const classes = useMemo(() => asArray(homeQuery.data?.classes), [homeQuery.data?.classes])
  const categoryNodes = useMemo(() => asArray(homeQuery.data?.categories), [homeQuery.data?.categories])
  const userOrgId = homeQuery.data?.userOrgId
  const isPersonalMember = String(user?.orgType || '').toUpperCase() === 'PERSONAL'
  const canFilterOrgCourses = Boolean(userOrgId && !isPersonalMember)
  const selectedClass = classes.find((item) => item.id === selectedClassId) || null
  const enrolledClasses = classes.filter((item) => item.enrolled)
  const inProgressClasses = enrolledClasses.filter((item) => Number(item.progressRate || 0) > 0 && Number(item.progressRate || 0) < 100)
  const completedClasses = enrolledClasses.filter((item) => Number(item.progressRate || 0) >= 100)
  const requiredTodo = classes.filter((item) => item.required && Number(item.progressRate || 0) < 100)
  const recentClasses = enrolledClasses.filter((item) => item.lastViewedAt).sort((a, b) => String(b.lastViewedAt).localeCompare(String(a.lastViewedAt)))

  React.useEffect(() => {
    if (categoryFilter === 'ALL') return
    if (typeof categoryFilter === 'string' && categoryFilter.startsWith('cat:')) return
    setCategoryFilter('ALL')
  }, [categoryFilter])

  const detailQuery = useQuery({
    queryKey: ['education-class', selectedClass?.id],
    queryFn: async () => unwrap(await api.get(`/education/classes/${selectedClass.id}`)),
    enabled: Boolean(selectedClass?.id),
  })
  const lessons = useMemo(() => asArray(detailQuery.data?.lessons), [detailQuery.data?.lessons])
  const selectedLesson = lessons.find((lesson) => lesson.id === selectedLessonId) || null
  const lessonQuery = useQuery({
    queryKey: ['education-lesson', selectedClass?.id, selectedLesson?.id],
    queryFn: async () => unwrap(await api.get(`/education/classes/${selectedClass.id}/lessons/${selectedLesson.id}`)),
    enabled: Boolean(selectedClass?.id && selectedLesson?.id),
  })

  React.useEffect(() => {
    if (!selectedClass?.enrolled || selectedLessonId || lessons.length === 0) return
    const nextLesson = lessons.find((lesson) => !lesson.completed) || lessons[0]
    if (nextLesson?.id) setSelectedLessonId(nextLesson.id)
  }, [lessons, selectedClass?.enrolled, selectedLessonId])

  React.useEffect(() => setCommentText(''), [selectedLessonId])

  React.useEffect(() => {
    if (!canFilterOrgCourses && orgOnly) setOrgOnly(false)
  }, [canFilterOrgCourses, orgOnly])

  const completeAndFocusComment = async () => {
    if (!lessonQuery.data?.id) return
    const nextLessonId = lessonQuery.data?.nextLessonId
    await completeMutation.mutateAsync(lessonQuery.data.id)
    if (nextLessonId) {
      setSelectedLessonId(nextLessonId)
      setLessonDetailTab('notes')
      return
    }
    setCommentText('유익한 강의 감사합니다.')
    setLessonDetailTab('comments')
    window.setTimeout(() => commentInputRef.current?.focus(), 0)
  }
  const enrollMutation = useMutation({
    mutationFn: async (classId) => unwrap(await api.post(`/education/classes/${classId}/enroll`)),
    onSuccess: async (data) => {
      toast.show('수강신청이 완료되었습니다.', 'success')
      if (data?.id) setSelectedClassId(data.id)
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('수강신청에 실패했습니다.', 'error'),
  })
  const completeMutation = useMutation({
    mutationFn: async (lessonId) => unwrap(await api.post(`/education/lessons/${lessonId}/complete`)),
    onSuccess: async () => {
      toast.show('수강 완료로 기록했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
      await queryClient.invalidateQueries({ queryKey: ['education-class', selectedClass?.id] })
      await queryClient.invalidateQueries({ queryKey: ['education-lesson', selectedClass?.id, selectedLesson?.id] })
    },
    onError: () => toast.show('수강 완료 처리에 실패했습니다.', 'error'),
  })
  const memoMutation = useMutation({
    mutationFn: async ({ lessonId, memo }) => unwrap(await api.put(`/education/lessons/${lessonId}/memo`, { memo })),
    onSuccess: async () => {
      toast.show('노트를 저장했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-lesson', selectedClass?.id, selectedLesson?.id] })
    },
    onError: () => toast.show('노트 저장에 실패했습니다.', 'error'),
  })
  const commentMutation = useMutation({
    mutationFn: async ({ lessonId, body }) => unwrap(await api.post(`/education/lessons/${lessonId}/comments`, { body })),
    onSuccess: async () => {
      setCommentText('')
      toast.show('댓글을 등록했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-lesson', selectedClass?.id, selectedLesson?.id] })
    },
    onError: () => toast.show('댓글 등록에 실패했습니다.', 'error'),
  })

  const filteredCatalogAllClasses = useMemo(() => {
    let out = mode === 'my' ? enrolledClasses : classes
    if (orgOnly && canFilterOrgCourses) out = out.filter((item) => item.scope === 'ORGANIZATION' && item.orgId === userOrgId)
    if ((!mode || mode === 'catalog') && inProgressOnly) {
      out = out.filter((item) => item.enrolled && Number(item.progressRate || 0) > 0 && Number(item.progressRate || 0) < 100)
    }
    const keyword = searchText.trim().toLowerCase()
    if (keyword) {
      out = out.filter((item) =>
        `${item.title || ''} ${item.description || ''} ${item.category || ''} ${item.educationCategoryTitle || ''}`.toLowerCase().includes(keyword),
      )
    }
    if (mode === 'my' && myStatusFilter === 'progress') out = out.filter((item) => Number(item.progressRate || 0) > 0 && Number(item.progressRate || 0) < 100)
    if (mode === 'my' && myStatusFilter === 'completed') out = out.filter((item) => Number(item.progressRate || 0) >= 100)
    if (requiredOnly) out = out.filter((item) => Boolean(item.required))
    return out
  }, [canFilterOrgCourses, classes, enrolledClasses, inProgressOnly, mode, myStatusFilter, orgOnly, requiredOnly, searchText, userOrgId])

  const selectedCategoryNode = useMemo(() => {
    if (categoryFilter === 'ALL' || typeof categoryFilter !== 'string' || !categoryFilter.startsWith('cat:')) return null
    const raw = Number(categoryFilter.slice(4))
    if (!Number.isFinite(raw)) return null
    return categoryNodes.find((n) => n.id === raw) || null
  }, [categoryFilter, categoryNodes])

  const catalogCategoryChildClasses = useMemo(() => {
    if (!selectedCategoryNode?.classes?.length) return []
    const raw = selectedCategoryNode.classes
    if (!requiredOnly) return raw
    return raw.filter((c) => Boolean(c.required))
  }, [selectedCategoryNode, requiredOnly])

  const bucketOnlyCatalogClassId = useMemo(() => {
    if (!catalogCategoryChildClasses.length) return null
    const regular = catalogCategoryChildClasses.filter((c) => !c.lessonBucket)
    if (regular.length > 0) return null
    return catalogCategoryChildClasses.find((c) => c.lessonBucket)?.id ?? null
  }, [catalogCategoryChildClasses])

  const bucketCatalogLessonsQuery = useQuery({
    queryKey: ['education-catalog-bucket-lessons', bucketOnlyCatalogClassId],
    queryFn: async () => unwrap(await api.get(`/education/classes/${bucketOnlyCatalogClassId}`)),
    enabled: Boolean(bucketOnlyCatalogClassId),
  })

  const catalogCategoryGridModel = useMemo(() => {
    if (!selectedCategoryNode) return { kind: 'none' }
    const child = catalogCategoryChildClasses
    if (!child.length) return { kind: 'classCards', rows: [] }
    const regular = child.filter((c) => !c.lessonBucket)
    const bucket = child.find((c) => c.lessonBucket)
    if (regular.length === 0 && bucket) {
      if (bucketCatalogLessonsQuery.isLoading) return { kind: 'loading' }
      const lessonsList = asArray(bucketCatalogLessonsQuery.data?.lessons)
      if (lessonsList.length === 0) return { kind: 'emptyBucket', bucket }
      return { kind: 'lessonCards', bucket, lessons: lessonsList }
    }
    const rows = [...regular.map((c) => ({ type: 'class', item: c }))]
    if (bucket) rows.push({ type: 'class', item: bucket })
    return { kind: 'classCards', rows }
  }, [selectedCategoryNode, catalogCategoryChildClasses, bucketCatalogLessonsQuery.isLoading, bucketCatalogLessonsQuery.data?.lessons])

  const catalogGridShowsLoading = categoryFilter !== 'ALL' && catalogCategoryGridModel.kind === 'loading'
  const catalogGridIsEmpty =
    catalogGridShowsLoading
      ? false
      : categoryFilter === 'ALL'
        ? filteredCatalogAllClasses.length === 0
        : !selectedCategoryNode ||
          catalogCategoryGridModel.kind === 'emptyBucket' ||
          (catalogCategoryGridModel.kind === 'classCards' && !(catalogCategoryGridModel.rows || []).length)

  if (homeQuery.isLoading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">교육 정보를 불러오는 중입니다.</div>
  }

  const watermarkBase = educationWatermarkBase(user)

  if (selectedClass) {
    return (
      <div className="space-y-5">
        <button type="button" onClick={() => { setSelectedClassId(null); setSelectedLessonId(null) }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">목록으로 돌아가기</button>
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 space-y-5">
              <EducationVideoFrame
                lesson={lessonQuery.data}
                fallbackThumbnailUrl={detailQuery.data?.thumbnailUrl || selectedClass.thumbnailUrl}
                watermarkBase={watermarkBase}
              />
              <div>
                <div className="text-xs font-bold text-brand-navy">카테고리</div>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">{catalogClassDisplayTitle(selectedClass)}</h2>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1">{enrollmentLabel(selectedClass.enrollmentStatus)}</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1">총 {selectedClass.totalLessons || 0}강</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1">조회 {selectedClass.viewedCount || 0}</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1">{periodLabel(selectedClass)}</span>
                </div>
                {(detailQuery.data?.intro || selectedClass.description) && <p className="mt-4 text-sm leading-6 text-slate-600">{detailQuery.data?.intro || selectedClass.description}</p>}
                {detailQuery.data?.notice && <div className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900"><b>공지사항</b> {detailQuery.data.notice}</div>}
                {selectedClass.enrolled ? (
                  <div className="mt-5">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500"><span>진도율</span><span className="text-brand-navy">{selectedClass.progressRate || 0}%</span></div>
                    <div className="mt-2"><ProgressBar value={selectedClass.progressRate} /></div>
                  </div>
                ) : (
                  <button type="button" onClick={() => enrollMutation.mutate(selectedClass.id)} disabled={enrollMutation.isPending} className={`mt-5 ${educationButtonClass.create}`}>
                    {enrollMutation.isPending ? '신청 중...' : '수강신청'}
                  </button>
                )}
              </div>
            </div>
            <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3"><h3 className="text-lg font-bold text-slate-900">강의 목록</h3><span className="text-xs font-semibold text-slate-500">{lessons.length}개</span></div>
              <div className="mt-3 divide-y divide-slate-100">
                {lessons.length === 0 ? <div className="py-6 text-sm text-slate-500">등록된 공개 강의가 없습니다.</div> : lessons.map((lesson) => {
                  const status = lessonStatusLabel(lesson)
                  return (
                    <button key={lesson.id} type="button" onClick={() => setSelectedLessonId(lesson.id)} disabled={!selectedClass.enrolled} className={`flex w-full gap-3 py-3 text-left text-sm hover:bg-slate-50 ${selectedLesson?.id === lesson.id ? educationSelectedClass.row : ''} disabled:cursor-not-allowed disabled:opacity-60`}>
                      <img src={lesson.thumbnailUrl || selectedClass.thumbnailUrl} alt="" className="h-16 w-24 shrink-0 rounded-lg bg-slate-100 object-cover" />
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 font-semibold text-slate-800">{lesson.sortOrder}. {lesson.title}</span>
                        <span className="mt-1 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                          <span className={`rounded-full px-2 py-0.5 ${status === '수강완료' ? 'bg-sky-100 text-brand-navy' : status === '수강중' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{status}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">조회 {lesson.viewedCount || 0}</span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </aside>
          </div>
        </section>
        {selectedClass.enrolled && (
          <LessonPlayer
            lesson={lessonQuery.data}
            commentText={commentText}
            setCommentText={setCommentText}
            completing={completeMutation.isPending}
            savingMemo={memoMutation.isPending}
            savingComment={commentMutation.isPending}
            activeTab={lessonDetailTab}
            setActiveTab={setLessonDetailTab}
            commentInputRef={commentInputRef}
            onComplete={completeAndFocusComment}
            onSaveMemo={(memo) => lessonQuery.data?.id && memoMutation.mutate({ lessonId: lessonQuery.data.id, memo })}
            onSaveComment={() => lessonQuery.data?.id && commentMutation.mutate({ lessonId: lessonQuery.data.id, body: commentText })}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {mode === 'my' && (
        <>
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[420px]">
                <div>
                  <div className="text-xs font-semibold text-slate-500">진행중</div>
                  <div className="mt-1 text-xl font-bold text-slate-900">{inProgressClasses.length}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">완료</div>
                  <div className="mt-1 text-xl font-bold text-slate-900">{completedClasses.length}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">필수 미완료</div>
                  <div className="mt-1 text-xl font-bold text-slate-900">{requiredTodo.length}</div>
                </div>
              </div>
              {recentClasses[0] && (
                <div className="flex min-w-0 flex-1 flex-col gap-2 border-t border-slate-100 pt-3 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-brand-navy">이어보기</div>
                      <div className="mt-1 truncate text-sm font-bold text-slate-900">{catalogClassDisplayTitle(recentClasses[0])}</div>
                      <div className="mt-1 text-xs text-slate-500">마지막 학습일 {formatDate(recentClasses[0].lastViewedAt)}</div>
                    </div>
                    <button type="button" onClick={() => setSelectedClassId(recentClasses[0].id)} className={educationButtonClass.secondary}>이어보기</button>
                  </div>
                  <ProgressBar value={recentClasses[0].progressRate} />
                </div>
              )}
            </div>
          </section>
          {myTab === 'notes' && <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">수업노트 모아보기는 강의별 개인 노트 저장 데이터를 기반으로 확장 가능합니다.</div>}
        </>
      )}
      {((!mode || mode === 'catalog') || (mode === 'my' && myTab === 'continue')) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {canFilterOrgCourses && (!mode || mode === 'catalog') ? (
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={orgOnly} onChange={(e) => setOrgOnly(e.target.checked)} />
                우리조직 강의만 보기
              </label>
            ) : null}
            {(!mode || mode === 'catalog') ? (
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={inProgressOnly} onChange={(e) => setInProgressOnly(e.target.checked)} />
                수강중인 강의만 보기
              </label>
            ) : null}
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={requiredOnly} onChange={(e) => setRequiredOnly(e.target.checked)} />
              필수 강의만 보기
            </label>
          </div>
          {(!mode || mode === 'catalog') ? (
            <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="검색어 입력" className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-brand-sky sm:w-96" />
          ) : null}
        </div>
      )}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[{ id: 'ALL', label: '전체 카테고리' }, ...categoryNodes.map((c) => ({ id: `cat:${c.id}`, label: c.title || '제목 없음' }))].map((category) => (
            <button key={category.id} type="button" onClick={() => setCategoryFilter(category.id)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${categoryFilter === category.id ? educationSelectedClass.tab : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{category.label}</button>
          ))}
        </div>
        {mode === 'my' && (
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setMyTab((prev) => prev === 'notes' ? 'continue' : 'notes')} className={myTab === 'notes' ? educationButtonClass.commit : educationButtonClass.secondary}>수업노트 모아보기</button>
            <select value={myStatusFilter} onChange={(e) => { setMyStatusFilter(e.target.value); setMyTab('continue') }} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-brand-sky">
              <option value="continue">이어보기</option>
              <option value="progress">진행중</option>
              <option value="completed">완료</option>
            </select>
          </div>
        )}
      </div>
      {(mode === 'my' && myTab === 'notes') ? null : catalogGridShowsLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">강의 목록을 불러오는 중입니다.</div>
      ) : catalogGridIsEmpty ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
          {categoryFilter !== 'ALL' && selectedCategoryNode ? '이 상위 그룹에 표시할 강의·클래스가 없습니다.' : '표시할 항목이 없습니다.'}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {categoryFilter === 'ALL'
            ? filteredCatalogAllClasses.map((item) => (
                <CourseCard key={item.id} item={item} enrolling={enrollMutation.isPending} onOpen={() => setSelectedClassId(item.id)} onEnroll={() => enrollMutation.mutate(item.id)} />
              ))
            : catalogCategoryGridModel.kind === 'lessonCards'
              ? catalogCategoryGridModel.lessons.map((lesson) => (
                  <LessonTeaserCard
                    key={lesson.id}
                    lesson={lesson}
                    classItem={catalogCategoryGridModel.bucket}
                    enrolling={enrollMutation.isPending}
                    onOpen={() => {
                      setSelectedClassId(catalogCategoryGridModel.bucket.id)
                      setSelectedLessonId(lesson.id)
                    }}
                    onEnroll={() => enrollMutation.mutate(catalogCategoryGridModel.bucket.id)}
                  />
                ))
              : (catalogCategoryGridModel.rows || []).map((row) => (
                  <CourseCard key={row.item.id} item={row.item} enrolling={enrollMutation.isPending} onOpen={() => setSelectedClassId(row.item.id)} onEnroll={() => enrollMutation.mutate(row.item.id)} />
                ))}
        </div>
      )}
    </div>
  )
}

function serializeEducationLessonPayload(form) {
  const raw = form.sortOrder
  let sortOrder
  if (raw === '' || raw == null) sortOrder = undefined
  else {
    const n = parseInt(String(raw), 10)
    sortOrder = Number.isFinite(n) ? Math.max(1, n) : undefined
  }
  return {
    title: form.title,
    description: form.description,
    youtubeUrl: form.youtubeUrl,
    materialName: form.materialName,
    materialUrl: form.materialUrl,
    publicFlag: form.publicFlag,
    ...(sortOrder !== undefined ? { sortOrder } : {}),
  }
}

function catCreateDraftKey(entry) {
  return entry?.kind === 'new' ? 'new' : `cat:${entry.id}`
}

function buildCatCreateOrderDraftFromCategories(categoriesList) {
  const sorted = [...categoriesList].sort((a, b) => {
    const sa = Number(a.sortOrder ?? 0)
    const sb = Number(b.sortOrder ?? 0)
    if (sa !== sb) return sa - sb
    return Number(a.id) - Number(b.id)
  })
  return [...sorted.map((c) => ({ kind: 'existing', id: c.id })), { kind: 'new' }]
}

/** 백엔드 EducationService.extractYoutubeVideoId / matchYoutubeVideoId 와 동등한 규칙(일부 URL 변형 포함) */
const YOUTUBE_PATH_ID_RE = /(?:youtu\.be\/|(?:m\.)?youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/i
const YOUTUBE_V_PARAM_RE = /[?&]v=([A-Za-z0-9_-]{11})(?:[&#]|$)/i

function extractYoutubeVideoIdClient(raw) {
  if (!raw || typeof raw !== 'string') return null
  const normalized = raw.replace(/&amp;/g, '&').trim()
  let m = normalized.match(YOUTUBE_PATH_ID_RE)
  if (m) return m[1]
  m = normalized.match(YOUTUBE_V_PARAM_RE)
  if (m) return m[1]
  try {
    const decoded = decodeURIComponent(normalized.replace(/\+/g, ' '))
    m = decoded.match(YOUTUBE_PATH_ID_RE) || decoded.match(YOUTUBE_V_PARAM_RE)
    if (m) return m[1]
  } catch {
    /* ignore */
  }
  if (/^[A-Za-z0-9_-]{11}$/.test(normalized)) return normalized
  return null
}

function isValidYoutubeUrl(raw) {
  return Boolean(extractYoutubeVideoIdClient(raw))
}

function lessonAddDraftKey(entry) {
  return entry?.kind === 'new' ? 'new' : `l:${entry.id}`
}

function buildLessonAddOrderDraftFromLessons(lessonsList) {
  const sorted = [...lessonsList].sort((a, b) => {
    const sa = Number(a.sortOrder ?? 0)
    const sb = Number(b.sortOrder ?? 0)
    if (sa !== sb) return sa - sb
    return Number(a.id) - Number(b.id)
  })
  return [...sorted.map((l) => ({ kind: 'existing', id: l.id })), { kind: 'new' }]
}

function AdminStepStrip({ labels, currentStep }) {
  return (
    <ol className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      {labels.map((label, idx) => {
        const stepNo = idx + 1
        const active = currentStep === stepNo
        const done = currentStep > stepNo
        return (
          <li
            key={label}
            className={`flex min-w-0 flex-1 items-center gap-3 rounded-2xl border px-3 py-3 sm:max-w-[240px] ${
              active ? 'border-sky-200 bg-sky-50 ring-1 ring-brand-sky/20' : done ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'
            }`}
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                active ? 'bg-brand-sky text-white' : done ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {stepNo}
            </span>
            <span className={`text-sm font-semibold leading-snug ${active ? 'text-brand-navy' : 'text-slate-700'}`}>{label}</span>
          </li>
        )
      })}
    </ol>
  )
}

function AdminCategoryPickerGrid({ categories, loading, selectedId, onSelect, emptyHint }) {
  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">불러오는 중입니다.</div>
  }
  if (categories.length === 0) {
    return <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">{emptyHint || '등록된 상위 카테고리가 없습니다. 먼저 상위 카테고리를 만드세요.'}</div>
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {categories.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className={`rounded-2xl border p-4 text-left transition hover:border-slate-300 ${
            selectedId === item.id ? educationSelectedClass.item : 'border-slate-200 bg-white'
          }`}
        >
          <div className="font-bold text-slate-900">{item.title || '제목 없음'}</div>
          <div className="mt-1 text-xs text-slate-500">{item.scope === 'PLATFORM' ? '설계EZ 공통' : item.orgName || '조직'} · {statusLabel(item.status)}</div>
        </button>
      ))}
    </div>
  )
}

function AdminClassPickerGrid({ classes, loading, selectedId, onSelect, emptyHint }) {
  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">불러오는 중입니다.</div>
  }
  if (classes.length === 0) {
    return <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">{emptyHint || '등록된 카테고리가 없습니다.'}</div>
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {classes.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className={`rounded-2xl border p-4 text-left transition hover:border-slate-300 ${
            selectedId === item.id ? educationSelectedClass.item : 'border-slate-200 bg-white'
          }`}
        >
          <div className="font-bold text-slate-900">{categoryName(item)}</div>
          <div className="mt-1 text-xs text-slate-500">{item.scope === 'PLATFORM' ? '설계EZ 공통' : item.orgName || '조직'} · {statusLabel(item.status)}</div>
        </button>
      ))}
    </div>
  )
}

function AdminEducationView() {
  const { hasAnyRole } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const isOperator = hasAnyRole([Roles.OPERATOR])
  const [adminSurface, setAdminSurface] = useState('hub')
  const [catCreateStep, setCatCreateStep] = useState(1)
  const [catCreateOrderDraft, setCatCreateOrderDraft] = useState([])
  const [dragCatCreateKey, setDragCatCreateKey] = useState(null)
  const [lessonAddStep, setLessonAddStep] = useState(1)
  const [lessonAddOrderDraft, setLessonAddOrderDraft] = useState([])
  const [dragLessonAddDraftKey, setDragLessonAddDraftKey] = useState(null)
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null)
  const [draggedLessonId, setDraggedLessonId] = useState(null)
  const [uploadingLessonMaterial, setUploadingLessonMaterial] = useState(false)
  const [isCreatingLesson, setIsCreatingLesson] = useState(false)
  const [classForm, setClassForm] = useState(emptyEducationClassForm)
  const [lessonForm, setLessonForm] = useState(emptyEducationLessonForm)
  const [pickerCategoryId, setPickerCategoryId] = useState(null)
  const [newClassTitle, setNewClassTitle] = useState('')
  const [classCreateStep, setClassCreateStep] = useState(1)
  const [classCreateCategoryId, setClassCreateCategoryId] = useState(null)
  const [classCreateForm, setClassCreateForm] = useState(emptyEducationClassCreateForm)

  const classesQuery = useQuery({ queryKey: ['education-admin-classes'], queryFn: async () => unwrap(await api.get('/education/admin/classes')) })
  const categoriesQuery = useQuery({ queryKey: ['education-admin-categories'], queryFn: async () => unwrap(await api.get('/education/admin/categories')) })
  const orgOptionsQuery = useQuery({
    queryKey: ['education-org-options'],
    queryFn: async () => unwrap(await api.get('/orgs/options', { params: { scope: 'all' } })),
  })
  const classes = useMemo(() => asCollection(classesQuery.data), [classesQuery.data])
  const categories = useMemo(() => asCollection(categoriesQuery.data), [categoriesQuery.data])
  const manageableClasses = useMemo(() => classes.filter((c) => !c.lessonBucket), [classes])
  const pickerCategory = useMemo(() => categories.find((c) => c.id === pickerCategoryId) || null, [categories, pickerCategoryId])
  const lessonManageNamedClasses = useMemo(() => {
    if (!pickerCategoryId) return []
    return classes.filter((c) => c.educationCategoryId === pickerCategoryId && !c.lessonBucket)
  }, [pickerCategoryId, classes])
  const lessonManageBucketId = pickerCategory?.directLessonsClassId ?? null
  const lessonManageSelectableIds = useMemo(() => {
    const ids = lessonManageNamedClasses.map((c) => c.id)
    if (lessonManageBucketId != null) ids.unshift(lessonManageBucketId)
    return ids
  }, [lessonManageNamedClasses, lessonManageBucketId])
  const orgOptions = useMemo(() => asCollection(orgOptionsQuery.data), [orgOptionsQuery.data])
  const selectedClass = useMemo(() => {
    const fromList = classes.find((item) => item.id === selectedClassId)
    if (fromList) return fromList
    if (!selectedClassId) return null
    const cat = categories.find((c) => c.directLessonsClassId === selectedClassId)
    if (!cat) return null
    return {
      id: selectedClassId,
      title: cat.title || '강의',
      category: '',
      educationCategoryId: cat.id,
      educationCategoryTitle: cat.title,
      orgId: cat.orgId,
      scope: cat.scope,
      orgName: cat.orgName,
      lessonBucket: true,
      status: cat.status || 'DRAFT',
      sharedOrgs: [],
    }
  }, [classes, selectedClassId, categories])
  const lessonTargetIsCategoryBucket = useMemo(
    () => Boolean(selectedClassId && categories.some((c) => c.directLessonsClassId === selectedClassId)),
    [selectedClassId, categories],
  )
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    description: '',
    status: 'DRAFT',
    scope: 'ORGANIZATION',
    orgId: '',
    sharedOrgIds: [],
  })
  const [orderedCategoryIds, setOrderedCategoryIds] = useState(null)
  const [dragCategoryOrderKey, setDragCategoryOrderKey] = useState(null)

  const selectedCategory = useMemo(() => categories.find((c) => c.id === selectedCategoryId) || null, [categories, selectedCategoryId])
  const bucketIdForCategoryEdit = selectedCategory?.directLessonsClassId
  const detailQuery = useQuery({
    queryKey: ['education-admin-class', selectedClassId],
    queryFn: async () => unwrap(await api.get(`/education/admin/classes/${selectedClassId}`)),
    enabled: Boolean(selectedClassId),
  })
  const bucketDetailQuery = useQuery({
    queryKey: ['education-admin-class', bucketIdForCategoryEdit],
    queryFn: async () => unwrap(await api.get(`/education/admin/classes/${bucketIdForCategoryEdit}`)),
    enabled: Boolean(bucketIdForCategoryEdit),
  })
  const statsQuery = useQuery({
    queryKey: ['education-admin-stats', selectedClassId],
    queryFn: async () => unwrap(await api.get(`/education/admin/classes/${selectedClassId}/stats`)),
    enabled: Boolean(selectedClassId && adminSurface === 'stats'),
  })
  const lessons = useMemo(() => asArray(detailQuery.data?.lessons), [detailQuery.data?.lessons])
  const selectedLesson = lessons.find((lesson) => lesson.id === selectedLessonId) || null

  const resetClassCreateForm = () => setClassCreateForm(emptyEducationClassCreateForm())

  const resetClassForm = () => setClassForm(emptyEducationClassForm())

  const goHub = () => {
    setAdminSurface('hub')
    setCatCreateStep(1)
    setCatCreateOrderDraft([])
    setDragCatCreateKey(null)
    setLessonAddStep(1)
    setLessonAddOrderDraft([])
    setDragLessonAddDraftKey(null)
    setIsCreatingLesson(false)
    setSelectedLessonId(null)
    setDraggedLessonId(null)
    setLessonForm(emptyEducationLessonForm())
    setPickerCategoryId(null)
    setNewClassTitle('')
    setClassCreateStep(1)
    setClassCreateCategoryId(null)
    resetClassCreateForm()
    setSelectedCategoryId(null)
    setOrderedCategoryIds(null)
    setDragCategoryOrderKey(null)
    setCategoryForm({
      title: '',
      description: '',
      status: 'DRAFT',
      scope: 'ORGANIZATION',
      orgId: '',
      sharedOrgIds: [],
    })
  }

  React.useEffect(() => {
    if (adminSurface !== 'stats' && adminSurface !== 'classManage') return
    if (!selectedClassId && manageableClasses[0]?.id) setSelectedClassId(manageableClasses[0].id)
  }, [adminSurface, manageableClasses, selectedClassId])

  React.useEffect(() => {
    if (adminSurface !== 'lessonAdd') return
    setSelectedLessonId(null)
    setLessonForm(emptyEducationLessonForm())
    setLessonAddOrderDraft([])
    setDragLessonAddDraftKey(null)
    setNewClassTitle('')
  }, [selectedClassId, adminSurface])

  React.useEffect(() => {
    if (adminSurface !== 'lessonManage') return
    setIsCreatingLesson(false)
    setSelectedLessonId(null)
    setLessonForm(emptyEducationLessonForm())
  }, [selectedClassId, adminSurface])

  React.useEffect(() => {
    if (adminSurface !== 'lessonManage') return
    if (pickerCategoryId != null || !categories.length) return
    setPickerCategoryId(categories[0].id)
  }, [adminSurface, pickerCategoryId, categories])

  React.useEffect(() => {
    if (adminSurface !== 'lessonManage') return
    if (!pickerCategoryId) {
      setSelectedClassId(null)
      return
    }
    const ids = lessonManageSelectableIds
    if (ids.length === 0) {
      setSelectedClassId(null)
      return
    }
    setSelectedClassId((cur) => (cur != null && ids.includes(cur) ? cur : ids[0]))
  }, [adminSurface, pickerCategoryId, lessonManageSelectableIds])

  React.useEffect(() => {
    if (adminSurface !== 'lessonManage') return
    if (isCreatingLesson || !selectedClass) return
    if (selectedLessonId || lessons.length === 0) return
    setSelectedLessonId(lessons[0].id)
  }, [adminSurface, selectedClassId, isCreatingLesson, lessons, selectedLessonId, selectedClass])

  React.useEffect(() => {
    if (isCreatingLesson || !selectedLesson) return
    setLessonForm({
      title: selectedLesson.title || '',
      description: selectedLesson.description || '',
      youtubeUrl: selectedLesson.youtubeUrl || '',
      materialName: selectedLesson.materialName || '',
      materialUrl: selectedLesson.materialUrl || '',
      publicFlag: selectedLesson.publicFlag !== false,
      sortOrder: selectedLesson.sortOrder ?? '',
    })
  }, [isCreatingLesson, selectedLesson])

  React.useEffect(() => {
    if (adminSurface !== 'classManage' || !selectedClass) return
    setClassForm({
      scope: selectedClass.scope || 'ORGANIZATION',
      orgId: selectedClass.orgId != null ? String(selectedClass.orgId) : '',
      title: selectedClass.title || '',
      description: selectedClass.description || '',
      intro: selectedClass.intro || '',
      notice: selectedClass.notice || '',
      sharedOrgIds: (selectedClass.sharedOrgs || []).map((org) => Number(org.orgId)).filter(Boolean),
      visibilityPlanCode: selectedClass.visibilityPlanCode || 'ALL',
      required: Boolean(selectedClass.required),
      status: selectedClass.status || 'DRAFT',
    })
  }, [adminSurface, selectedClass])

  React.useEffect(() => {
    if (adminSurface !== 'categoryManage') return
    setOrderedCategoryIds((prev) => {
      const next = categories.map((c) => c.id)
      if (prev === null) return next
      const prevSet = new Set(prev)
      const nextSet = new Set(next)
      if (prev.length !== next.length) return next
      for (const id of next) {
        if (!prevSet.has(id)) return next
      }
      for (const id of prev) {
        if (!nextSet.has(id)) return next
      }
      return prev
    })
  }, [adminSurface, categories])

  React.useEffect(() => {
    if (adminSurface !== 'categoryManage' || !selectedCategoryId) return
    const cat = categories.find((c) => c.id === selectedCategoryId)
    if (!cat) return
    setCategoryForm({
      title: cat.title || '',
      description: cat.description || '',
      status: cat.status || 'DRAFT',
      scope: cat.scope || 'ORGANIZATION',
      orgId: cat.orgId != null ? String(cat.orgId) : '',
      sharedOrgIds: [],
    })
  }, [adminSurface, selectedCategoryId, categories])

  React.useEffect(() => {
    if (adminSurface !== 'categoryManage' || !bucketDetailQuery.data || !selectedCategoryId) return
    const d = bucketDetailQuery.data
    if (d.educationCategoryId != null && Number(d.educationCategoryId) !== Number(selectedCategoryId)) return
    setCategoryForm((prev) => ({
      ...prev,
      sharedOrgIds: (d.sharedOrgs || []).map((o) => Number(o.orgId)).filter(Boolean),
    }))
  }, [adminSurface, bucketDetailQuery.data, selectedCategoryId])

  const createCategoryMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: classForm.title.trim(),
        description: classForm.description || '',
        status: classForm.status,
        sharedOrgIds: classForm.sharedOrgIds || [],
        scope: isOperator ? classForm.scope : 'ORGANIZATION',
        ...(isOperator && classForm.scope === 'ORGANIZATION' && classForm.orgId ? { orgId: Number(classForm.orgId) } : {}),
      }
      const created = unwrap(await api.post('/education/admin/categories', payload))
      const ids = catCreateOrderDraft.map((e) => (e.kind === 'new' ? created.id : e.id))
      unwrap(await api.post('/education/admin/categories/reorder', { categoryIds: ids }))
      return created
    },
    onSuccess: async (created) => {
      toast.show('상위 카테고리를 등록했습니다.', 'success')
      resetClassForm()
      setCatCreateStep(1)
      setOrderedCategoryIds(null)
      setAdminSurface('categoryManage')
      if (created?.id) setSelectedCategoryId(created.id)
      await queryClient.invalidateQueries({ queryKey: ['education-admin-categories'] })
      await queryClient.refetchQueries({ queryKey: ['education-admin-categories'] })
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('상위 카테고리 등록에 실패했습니다.', 'error'),
  })
  const updateClassMutation = useMutation({
    mutationFn: async () => unwrap(await api.put(`/education/admin/classes/${selectedClass.id}`, {
      ...classForm,
      scope: isOperator ? classForm.scope : 'ORGANIZATION',
      categoryId: selectedClass?.educationCategoryId,
      category: '',
      sharedOrgIds: classForm.sharedOrgIds,
    })),
    onSuccess: async () => {
      toast.show('클래스를 저장했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
      await queryClient.invalidateQueries({ queryKey: ['education-admin-class', selectedClassId] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('클래스 저장에 실패했습니다.', 'error'),
  })
  const createSubClassMutation = useMutation({
    mutationFn: async () => {
      const title = newClassTitle.trim()
      if (!pickerCategoryId || !title) throw new Error('invalid')
      return unwrap(await api.post('/education/admin/classes', {
        categoryId: pickerCategoryId,
        title,
        description: '',
        status: 'DRAFT',
        visibilityPlanCode: 'ALL',
        required: false,
        sharedOrgIds: [],
        category: '',
      }))
    },
    onSuccess: async (created) => {
      toast.show('새 클래스를 만들었습니다.', 'success')
      setNewClassTitle('')
      if (created?.id) setSelectedClassId(created.id)
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
    },
    onError: () => toast.show('클래스 생성에 실패했습니다.', 'error'),
  })
  const createStandaloneClassMutation = useMutation({
    mutationFn: async () => {
      if (!classCreateCategoryId || !classCreateForm.title.trim()) throw new Error('invalid')
      return unwrap(await api.post('/education/admin/classes', {
        categoryId: classCreateCategoryId,
        title: classCreateForm.title.trim(),
        description: classCreateForm.description || '',
        intro: classCreateForm.intro || '',
        notice: classCreateForm.notice || '',
        status: classCreateForm.status,
        visibilityPlanCode: classCreateForm.visibilityPlanCode || 'ALL',
        required: classCreateForm.required,
        sharedOrgIds: classCreateForm.sharedOrgIds || [],
        category: '',
      }))
    },
    onSuccess: async (created) => {
      toast.show('클래스를 등록했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
      setAdminSurface('classManage')
      setClassCreateStep(1)
      setClassCreateCategoryId(null)
      resetClassCreateForm()
      if (created?.id) setSelectedClassId(created.id)
    },
    onError: () => toast.show('클래스 등록에 실패했습니다.', 'error'),
  })
  const createLessonMutation = useMutation({
    mutationFn: async () => {
      const payload = serializeEducationLessonPayload({ ...lessonForm, sortOrder: '' })
      const created = unwrap(await api.post(`/education/admin/classes/${selectedClass.id}/lessons`, payload))
      const ids = lessonAddOrderDraft.map((e) => (e.kind === 'new' ? created.id : e.id))
      unwrap(await api.post(`/education/admin/classes/${selectedClass.id}/lessons/reorder`, { lessonIds: ids }))
      return created
    },
    onSuccess: async (created) => {
      toast.show('강의를 추가했습니다.', 'success')
      setIsCreatingLesson(false)
      setLessonAddStep(1)
      setLessonAddOrderDraft([])
      setAdminSurface('lessonManage')
      if (created?.id) setSelectedLessonId(created.id)
      await queryClient.invalidateQueries({ queryKey: ['education-admin-class', selectedClassId] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('강의 추가에 실패했습니다.', 'error'),
  })
  const updateLessonMutation = useMutation({
    mutationFn: async () => unwrap(await api.put(`/education/admin/lessons/${selectedLesson.id}`, serializeEducationLessonPayload(lessonForm))),
    onSuccess: async () => {
      toast.show('강의 정보를 저장했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-admin-class', selectedClassId] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('강의 정보 저장에 실패했습니다.', 'error'),
  })
  const reorderLessonsMutation = useMutation({
    mutationFn: async (lessonIds) => unwrap(await api.post(`/education/admin/classes/${selectedClass.id}/lessons/reorder`, { lessonIds })),
    onSuccess: async () => {
      toast.show('강의 순서를 저장했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-admin-class', selectedClassId] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('강의 순서 저장에 실패했습니다.', 'error'),
  })

  const updateCategoryMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCategoryId) throw new Error('none')
      const cat = categories.find((c) => c.id === selectedCategoryId)
      return unwrap(await api.put(`/education/admin/categories/${selectedCategoryId}`, {
        title: categoryForm.title.trim(),
        description: categoryForm.description || '',
        status: categoryForm.status,
        sortOrder: cat?.sortOrder ?? 1,
        sharedOrgIds: categoryForm.sharedOrgIds || [],
        scope: isOperator ? categoryForm.scope : 'ORGANIZATION',
        ...(isOperator && categoryForm.scope === 'ORGANIZATION' && categoryForm.orgId ? { orgId: Number(categoryForm.orgId) } : {}),
      }))
    },
    onSuccess: async () => {
      toast.show('카테고리를 저장했습니다.', 'success')
      await queryClient.invalidateQueries({ queryKey: ['education-admin-categories'] })
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('카테고리 저장에 실패했습니다.', 'error'),
  })

  const reorderCategoriesMutation = useMutation({
    mutationFn: async (categoryIds) => unwrap(await api.post('/education/admin/categories/reorder', { categoryIds })),
    onSuccess: async () => {
      toast.show('카테고리 순서를 저장했습니다.', 'success')
      setOrderedCategoryIds(null)
      await queryClient.invalidateQueries({ queryKey: ['education-admin-categories'] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('순서 저장에 실패했습니다.', 'error'),
  })

  const deleteClassMutation = useMutation({
    mutationFn: async (classId) => {
      unwrap(await api.delete(`/education/admin/classes/${classId}`))
    },
    onSuccess: async (_, classId) => {
      toast.show('클래스를 보관 처리했습니다.', 'success')
      setSelectedClassId((cur) => (cur === classId ? null : cur))
      await queryClient.invalidateQueries({ queryKey: ['education-admin-classes'] })
      await queryClient.invalidateQueries({ queryKey: ['education-admin-categories'] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('클래스 처리에 실패했습니다.', 'error'),
  })

  const deleteLessonMutation = useMutation({
    mutationFn: async (lessonId) => {
      unwrap(await api.delete(`/education/admin/lessons/${lessonId}`))
    },
    onSuccess: async (_, lessonId) => {
      toast.show('강의를 삭제했습니다.', 'success')
      setSelectedLessonId((cur) => (cur === lessonId ? null : cur))
      await queryClient.invalidateQueries({ queryKey: ['education-admin-class', selectedClassId] })
      await queryClient.invalidateQueries({ queryKey: ['education-home'] })
    },
    onError: () => toast.show('강의 삭제에 실패했습니다.', 'error'),
  })

  const startCategoryCreate = () => {
    resetClassForm()
    setSelectedClassId(null)
    setCatCreateStep(1)
    setCatCreateOrderDraft([])
    setDragCatCreateKey(null)
    setAdminSurface('catCreate')
  }

  const startClassCreate = () => {
    setClassCreateStep(1)
    setClassCreateCategoryId(null)
    resetClassCreateForm()
    setSelectedClassId(null)
    setSelectedLessonId(null)
    setAdminSurface('classCreate')
  }

  const moveCatCreateDraftRow = (targetKey) => {
    if (!dragCatCreateKey || dragCatCreateKey === targetKey) return
    setCatCreateOrderDraft((draft) => {
      const arr = [...draft]
      const fromIdx = arr.findIndex((e) => catCreateDraftKey(e) === dragCatCreateKey)
      const toIdx = arr.findIndex((e) => catCreateDraftKey(e) === targetKey)
      if (fromIdx < 0 || toIdx < 0) return draft
      const [moved] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, moved)
      return arr
    })
    setDragCatCreateKey(null)
  }

  const startLessonAdd = (fromManage = false) => {
    setSelectedLessonId(null)
    setNewClassTitle('')
    setLessonAddOrderDraft([])
    setDragLessonAddDraftKey(null)
    setIsCreatingLesson(true)
    setLessonForm(emptyEducationLessonForm())
    if (!fromManage) {
      setSelectedClassId(null)
      setPickerCategoryId(null)
      setLessonAddStep(1)
    } else if (selectedClassId) {
      const cls = classes.find((c) => c.id === selectedClassId)
      const bucketCat = categories.find((c) => c.directLessonsClassId === selectedClassId)
      const catId = cls?.educationCategoryId ?? bucketCat?.id ?? pickerCategoryId
      if (catId) setPickerCategoryId(catId)
      setLessonAddStep(2)
    } else {
      setPickerCategoryId(null)
      setLessonAddStep(1)
    }
    setAdminSurface('lessonAdd')
  }

  const moveLessonAddDraftRow = (targetKey) => {
    if (!dragLessonAddDraftKey || dragLessonAddDraftKey === targetKey) return
    setLessonAddOrderDraft((draft) => {
      const arr = [...draft]
      const fromIdx = arr.findIndex((e) => lessonAddDraftKey(e) === dragLessonAddDraftKey)
      const toIdx = arr.findIndex((e) => lessonAddDraftKey(e) === targetKey)
      if (fromIdx < 0 || toIdx < 0) return draft
      const [moved] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, moved)
      return arr
    })
    setDragLessonAddDraftKey(null)
  }

  const startLessonManage = () => {
    setSelectedClassId(null)
    setPickerCategoryId(null)
    setNewClassTitle('')
    setAdminSurface('lessonManage')
  }

  const startCategoryManage = () => {
    setSelectedCategoryId(null)
    setOrderedCategoryIds(null)
    setAdminSurface('categoryManage')
  }

  const startClassManage = () => {
    setSelectedClassId(null)
    setAdminSurface('classManage')
  }

  const moveCategoryOrderRow = (targetId) => {
    if (!dragCategoryOrderKey || dragCategoryOrderKey === targetId || !orderedCategoryIds?.length) return
    setOrderedCategoryIds((ids) => {
      const arr = [...ids]
      const fromIdx = arr.indexOf(dragCategoryOrderKey)
      const toIdx = arr.indexOf(targetId)
      if (fromIdx < 0 || toIdx < 0) return ids
      const [moved] = arr.splice(fromIdx, 1)
      arr.splice(toIdx, 0, moved)
      return arr
    })
    setDragCategoryOrderKey(null)
  }

  const uploadLessonMaterial = async (file) => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    setUploadingLessonMaterial(true)
    try {
      const payload = unwrap(await api.post('/files/upload/education-material', formData, {
        params: { orgId: selectedClass?.orgId || 0 },
        headers: { 'Content-Type': 'multipart/form-data' },
      }))
      const fileId = payload?.fileId || payload?.id
      if (!fileId) throw new Error('fileId missing')
      setLessonForm((prev) => ({
        ...prev,
        materialName: payload?.originalName || file.name,
        materialUrl: `/v1/files/${encodeURIComponent(fileId)}/download`,
      }))
      toast.show('강의 자료를 업로드했습니다.', 'success')
    } catch {
      toast.show('강의 자료 업로드에 실패했습니다.', 'error')
    } finally {
      setUploadingLessonMaterial(false)
    }
  }

  const selectLesson = (lessonId) => {
    setIsCreatingLesson(false)
    setSelectedLessonId(lessonId)
  }

  const moveLesson = (targetLessonId) => {
    if (!draggedLessonId || draggedLessonId === targetLessonId || !selectedClass) return
    const currentIds = lessons.map((lesson) => lesson.id)
    const fromIndex = currentIds.indexOf(draggedLessonId)
    const toIndex = currentIds.indexOf(targetLessonId)
    if (fromIndex < 0 || toIndex < 0) return
    const nextIds = [...currentIds]
    const [moved] = nextIds.splice(fromIndex, 1)
    nextIds.splice(toIndex, 0, moved)
    setDraggedLessonId(null)
    reorderLessonsMutation.mutate(nextIds)
  }

  const classManageSidebar = (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900">클래스</h2>
        <button type="button" onClick={startClassCreate} className={educationButtonClass.createSmall}>
          새 클래스
        </button>
      </div>
      {classesQuery.isLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">불러오는 중입니다.</div>
      ) : manageableClasses.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">등록된 교육 클래스가 없습니다. 새 클래스를 만들거나 상위 카테고리를 확인하세요.</div>
      ) : (
        manageableClasses.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelectedClassId(item.id)}
            className={`block w-full rounded-2xl border p-4 text-left ${selectedClass?.id === item.id ? educationSelectedClass.item : 'border-slate-200 bg-white'}`}
          >
            <div className="font-bold text-slate-900">{categoryName(item)}</div>
            {item.educationCategoryTitle ? <div className="mt-0.5 text-[11px] font-semibold text-slate-400">상위 · {item.educationCategoryTitle}</div> : null}
            <div className="mt-1 text-xs text-slate-500">{item.scope === 'PLATFORM' ? '설계EZ 공통' : item.orgName || '조직'} · {statusLabel(item.status)}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
              <span>신청 {item.classEnrollmentCount || 0}명</span>
              <span>완료 {item.classCompletedCount || 0}명</span>
              <span>대상 {item.visibilityPlanLabel || educationVisibilityPlanLabel(item.visibilityPlanCode)}</span>
              <span>공유 {(item.sharedOrgs || []).length}개 조직</span>
            </div>
          </button>
        ))
      )}
    </section>
  )

  const catCreateLabels = ['카테고리 명', '표시 순서', '공개 · 공유 · 저장']
  const classCreateLabels = ['상위·클래스', '공개 · 대상 · 저장']
  const lessonAddLabels = ['상위·클래스 선택', '강의 내용', '강의 순서', '공개 여부 · 등록']
  if (adminSurface === 'hub') {
    return (
      <div className="space-y-8">
        <div>
          <p className="text-sm font-medium text-slate-700">상위 카테고리 · 교육 클래스 · 강의를 각각 묶어 관리합니다. 목록에서 고르고 생성·수정·순서·보관까지 한 화면에서 처리할 수 있습니다.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <button
            type="button"
            onClick={startCategoryManage}
            className="group flex flex-col rounded-3xl border-2 border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-teal-300 hover:shadow-md"
          >
            <span className="mb-4 h-1 w-14 rounded-full bg-teal-600" />
            <h3 className="text-xl font-bold text-slate-900">카테고리 관리</h3>
            <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">상위 그룹을 만들고, 순서를 정하고, 이름·공개·공유 조직을 수정합니다. (보관 시 비노출)</p>
            <span className="mt-6 inline-flex w-fit rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800 group-hover:bg-teal-100">열기</span>
          </button>
          <button
            type="button"
            onClick={startClassManage}
            className="group flex flex-col rounded-3xl border-2 border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md"
          >
            <span className="mb-4 h-1 w-14 rounded-full bg-emerald-600" />
            <h3 className="text-xl font-bold text-slate-900">클래스 관리</h3>
            <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">클래스를 고르고 소개·공지·필수 여부·공개·공유를 수정합니다. 새 클래스를 만들거나 보관할 수 있습니다.</p>
            <span className="mt-6 inline-flex w-fit rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900 group-hover:bg-emerald-100">열기</span>
          </button>
          <button
            type="button"
            onClick={startLessonManage}
            className="group flex flex-col rounded-3xl border-2 border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-sky-200 hover:shadow-md"
          >
            <span className="mb-4 h-1 w-14 rounded-full bg-brand-sky" />
            <h3 className="text-xl font-bold text-slate-900">강의 관리</h3>
            <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">클래스를 고른 뒤 강의 순서·내용을 다루고, 필요하면 새 강의 등록 마법사로 이동합니다.</p>
            <span className="mt-6 inline-flex w-fit rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-brand-navy group-hover:bg-sky-100">열기</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-slate-200 pt-5 text-sm">
          <button type="button" onClick={() => { setAdminSurface('stats'); setSelectedClassId(null) }} className="font-semibold text-slate-700 hover:underline">
            수강 현황 통계 (클래스별)
          </button>
        </div>
      </div>
    )
  }

  if (adminSurface === 'categoryManage') {
    const orderedCategories = (orderedCategoryIds || [])
      .map((id) => categories.find((c) => c.id === id))
      .filter(Boolean)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">카테고리 관리</h2>
        </div>
        <div className="grid gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold text-slate-800">상위 카테고리</h3>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => orderedCategoryIds?.length && reorderCategoriesMutation.mutate(orderedCategoryIds)}
                  disabled={!orderedCategoryIds?.length || reorderCategoriesMutation.isPending}
                  className={educationButtonClass.outline}
                >
                  {reorderCategoriesMutation.isPending ? '저장…' : '순서 저장'}
                </button>
                <button type="button" onClick={startCategoryCreate} className={educationButtonClass.createSmall}>추가</button>
              </div>
            </div>
            <p className="text-xs text-slate-500">행을 드래그해 순서를 바꾼 뒤 「순서 저장」을 누르세요.</p>
            <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
              {categoriesQuery.isLoading ? (
                <div className="p-4 text-sm text-slate-500">불러오는 중입니다.</div>
              ) : orderedCategories.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">등록된 상위 카테고리가 없습니다. 「추가」로 만듭니다.</div>
              ) : (
                orderedCategories.map((cat, idx) => (
                  <button
                    key={cat.id}
                    type="button"
                    draggable
                    onClick={() => setSelectedCategoryId(cat.id)}
                    onDragStart={() => setDragCategoryOrderKey(cat.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => moveCategoryOrderRow(cat.id)}
                    onDragEnd={() => setDragCategoryOrderKey(null)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${selectedCategoryId === cat.id ? educationSelectedClass.row : 'hover:bg-slate-50'} ${dragCategoryOrderKey === cat.id ? 'opacity-60' : ''}`}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">{idx + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-slate-900">{cat.title}</span>
                      <span className="mt-0.5 block text-[11px] text-slate-500">{cat.scope === 'PLATFORM' ? '플랫폼' : cat.orgName || '조직'} · {statusLabel(cat.status)}</span>
                    </span>
                    <span className="shrink-0 text-slate-300" title="드래그해 순서 변경">☰</span>
                  </button>
                ))
              )}
            </div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            {!selectedCategoryId ? (
              <div className="py-12 text-center text-sm text-slate-500">왼쪽에서 카테고리를 선택하면 수정할 수 있습니다.</div>
            ) : (
              <div className="space-y-5">
                <div className="border-b border-slate-100 pb-4">
                  <div className="text-xs font-bold text-brand-navy">선택</div>
                  <h3 className="mt-1 text-xl font-bold text-slate-900">{selectedCategory?.title}</h3>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">상태</label>
                  <select
                    value={categoryForm.status}
                    onChange={(e) => setCategoryForm((p) => ({ ...p, status: e.target.value }))}
                    className="mt-1 w-full max-w-xs rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
                  >
                    <option value="DRAFT">비공개(초안)</option>
                    <option value="PUBLISHED">공개</option>
                    <option value="ARCHIVED">보관</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">보관은 삭제 대신 비노출 처리에 가깝습니다.</p>
                </div>
                {isOperator && (
                  <select value={categoryForm.scope} onChange={(e) => setCategoryForm((p) => ({ ...p, scope: e.target.value }))} className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none">
                    <option value="ORGANIZATION">조직 범위</option>
                    <option value="PLATFORM">플랫폼 공통</option>
                  </select>
                )}
                {isOperator && categoryForm.scope === 'ORGANIZATION' && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">소속 조직</label>
                    <select value={categoryForm.orgId} onChange={(e) => setCategoryForm((p) => ({ ...p, orgId: e.target.value }))} className="mt-1 w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none">
                      <option value="">조직 선택</option>
                      {orgOptions.map((org) => (
                        <option key={org.id} value={String(org.id)}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-700">이름</label>
                  <input value={categoryForm.title} onChange={(e) => setCategoryForm((p) => ({ ...p, title: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">설명</label>
                  <textarea value={categoryForm.description} onChange={(e) => setCategoryForm((p) => ({ ...p, description: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
                </div>
                <OrgShareSelector orgOptions={orgOptions} selectedIds={categoryForm.sharedOrgIds} onChange={(sharedOrgIds) => setCategoryForm((p) => ({ ...p, sharedOrgIds }))} />
                <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                  <button type="button" onClick={() => updateCategoryMutation.mutate()} disabled={updateCategoryMutation.isPending || !categoryForm.title.trim()} className={educationButtonClass.commit}>
                    {updateCategoryMutation.isPending ? '저장 중…' : '저장'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    )
  }

  if (adminSurface === 'catCreate') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">카테고리 생성</h2>
        </div>
        <AdminStepStrip labels={catCreateLabels} currentStep={catCreateStep} />
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          {catCreateStep === 1 && (
            <div className="grid max-w-xl gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-brand-navy">Step 1</div>
                <h3 className="mt-1 text-lg font-bold text-slate-900">상위 카테고리 이름</h3>
                <p className="mt-1 text-sm text-slate-500">그 아래에 여러 교육 클래스를 두거나, 클래스 없이 강의만 둘 수 있습니다.</p>
              </div>
              {isOperator && (
                <div>
                  <label className="text-sm font-medium text-slate-700">노출 범위</label>
                  <select value={classForm.scope} onChange={(e) => setClassForm((prev) => ({ ...prev, scope: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none">
                    <option value="ORGANIZATION">조직 카테고리</option>
                    <option value="PLATFORM">설계EZ 공통</option>
                  </select>
                </div>
              )}
              {isOperator && classForm.scope === 'ORGANIZATION' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">소속 조직</label>
                  <select
                    value={classForm.orgId}
                    onChange={(e) => setClassForm((prev) => ({ ...prev, orgId: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
                  >
                    <option value="">조직 선택</option>
                    {orgOptions.map((org) => (
                      <option key={org.id} value={String(org.id)}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-700">상위 카테고리 명</label>
                <input value={classForm.title} onChange={(e) => setClassForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="예: 신입 설계사 온보딩" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!classForm.title.trim()) return
                    if (isOperator && classForm.scope === 'ORGANIZATION' && !classForm.orgId) return
                    setCatCreateOrderDraft(buildCatCreateOrderDraftFromCategories(categories))
                    setCatCreateStep(2)
                  }}
                  disabled={!classForm.title.trim() || (isOperator && classForm.scope === 'ORGANIZATION' && !classForm.orgId)}
                  className={educationButtonClass.secondary}
                >
                  다음
                </button>
              </div>
            </div>
          )}
          {catCreateStep === 2 && (
            <div className="grid max-w-2xl gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-brand-navy">Step 2</div>
                <h3 className="mt-1 text-lg font-bold text-slate-900">표시 순서</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  등록된 상위 카테고리와 이번에 추가할 <b>「{classForm.title || '신규'}」</b>의 위치를 한 목록에서 드래그해 정합니다. 맨 위가 먼저 노출됩니다.
                </p>
              </div>
              <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-slate-50/50">
                {catCreateOrderDraft.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500">목록을 불러오는 중이거나 카테고리가 없습니다. 이전 단계로 돌아가 이름을 확인하세요.</div>
                ) : catCreateOrderDraft.map((entry, idx) => {
                  const key = catCreateDraftKey(entry)
                  const label = entry.kind === 'new'
                    ? `신규 · ${classForm.title || '제목 없음'}`
                    : (categories.find((c) => c.id === entry.id)?.title || `상위 #${entry.id}`)
                  return (
                    <div
                      key={key}
                      draggable
                      onDragStart={() => setDragCatCreateKey(key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => moveCatCreateDraftRow(key)}
                      onDragEnd={() => setDragCatCreateKey(null)}
                      className={`flex w-full cursor-grab items-center gap-3 px-4 py-3 text-left text-sm active:cursor-grabbing ${dragCatCreateKey === key ? 'opacity-60' : ''} ${entry.kind === 'new' ? 'bg-teal-50/80' : 'hover:bg-white'}`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">{idx + 1}</span>
                      <span className="min-w-0 flex-1 font-semibold text-slate-900">{label}</span>
                      <span className="shrink-0 text-slate-400" title="드래그해서 순서 변경">☰</span>
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setCatCreateStep(1)} className={educationButtonClass.outline}>이전</button>
                <button type="button" onClick={() => catCreateOrderDraft.length > 0 && setCatCreateStep(3)} disabled={catCreateOrderDraft.length === 0} className={educationButtonClass.secondary}>
                  다음
                </button>
              </div>
            </div>
          )}
          {catCreateStep === 3 && (
            <div className="grid max-w-2xl gap-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-brand-navy">Step 3</div>
                <h3 className="mt-1 text-lg font-bold text-slate-900">공개 및 공유</h3>
                <p className="mt-1 text-sm text-slate-500">수강자에게 공개할지, 다른 조직에 공유할지를 정합니다.</p>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
                <input type="checkbox" checked={classForm.status === 'PUBLISHED'} onChange={(e) => setClassForm((prev) => ({ ...prev, status: e.target.checked ? 'PUBLISHED' : 'DRAFT' }))} />
                카테고리를 공개합니다
              </label>
              <OrgShareSelector orgOptions={orgOptions} selectedIds={classForm.sharedOrgIds} onChange={(sharedOrgIds) => setClassForm((prev) => ({ ...prev, sharedOrgIds }))} />
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <div className="font-semibold text-slate-800">요약</div>
                <div className="mt-2"><span className="font-medium text-slate-700">이름</span> · {classForm.title || '—'}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">표시 순서</span> · {catCreateOrderDraft.map((e) => (e.kind === 'new' ? `신규:${classForm.title || '?'}` : (categories.find((c) => c.id === e.id)?.title || `상위#${e.id}`))).join(' → ')}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">공개</span> · {classForm.status === 'PUBLISHED' ? '예' : '아니오 (비공개)'}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">공유 조직</span> · {classForm.sharedOrgIds?.length ? `${classForm.sharedOrgIds.length}곳` : '없음'}</div>
                <p className="mt-3 text-xs text-slate-500">필수 교육 여부는 각 <span className="font-semibold text-slate-700">클래스</span>를 만들거나 수정할 때 설정합니다.</p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setCatCreateStep(2)} className={educationButtonClass.outline}>이전</button>
                <button type="button" onClick={() => createCategoryMutation.mutate()} disabled={createCategoryMutation.isPending || !classForm.title.trim() || catCreateOrderDraft.length === 0} className={educationButtonClass.commit}>
                  {createCategoryMutation.isPending ? '저장 중...' : '상위 카테고리 저장'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    )
  }

  if (adminSurface === 'classCreate') {
    const selectedCat = categories.find((c) => c.id === classCreateCategoryId)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">클래스 생성</h2>
        </div>
        <AdminStepStrip labels={classCreateLabels} currentStep={classCreateStep} />
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          {classCreateStep === 1 && (
            <div className="grid max-w-2xl gap-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-brand-navy">Step 1</div>
                <h3 className="mt-1 text-lg font-bold text-slate-900">상위 카테고리와 클래스 정보</h3>
                <p className="mt-1 text-sm text-slate-500">클래스가 속할 교육 상위 그룹을 고른 뒤, 수강자에게 보일 이름과 소개를 입력합니다.</p>
              </div>
              <AdminCategoryPickerGrid
                categories={categories}
                loading={categoriesQuery.isLoading}
                selectedId={classCreateCategoryId}
                onSelect={setClassCreateCategoryId}
              />
              <div>
                <label className="text-sm font-medium text-slate-700">클래스명</label>
                <input value={classCreateForm.title} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="예: 신입 온보딩 필수" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">짧은 설명 (선택)</label>
                <textarea value={classCreateForm.description} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, description: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">소개 (선택)</label>
                <textarea value={classCreateForm.intro} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, intro: e.target.value }))} rows={4} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">공지 (선택)</label>
                <textarea value={classCreateForm.notice} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, notice: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              </div>
              {selectedCat ? (
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  선택한 상위: <span className="font-semibold text-slate-800">{selectedCat.title}</span>
                  {' · '}
                  {selectedCat.scope === 'PLATFORM' ? '설계EZ 공통' : selectedCat.orgName || '조직'}
                </div>
              ) : null}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => classCreateCategoryId && classCreateForm.title.trim() && setClassCreateStep(2)}
                  disabled={!classCreateCategoryId || !classCreateForm.title.trim()}
                  className={educationButtonClass.secondary}
                >
                  다음
                </button>
              </div>
            </div>
          )}
          {classCreateStep === 2 && (
            <div className="grid max-w-2xl gap-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-brand-navy">Step 2</div>
                <h3 className="mt-1 text-lg font-bold text-slate-900">공개 및 대상</h3>
                <p className="mt-1 text-sm text-slate-500">노출 여부·대상 플랜·다른 조직 공유·필수 여부를 정합니다.</p>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
                <input type="checkbox" checked={classCreateForm.status === 'PUBLISHED'} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, status: e.target.checked ? 'PUBLISHED' : 'DRAFT' }))} />
                클래스를 공개합니다
              </label>
              <div>
                <label className="text-sm font-medium text-slate-700">대상 플랜</label>
                {educationVisibilityPlanSelect(classCreateForm.visibilityPlanCode, (visibilityPlanCode) => setClassCreateForm((prev) => ({ ...prev, visibilityPlanCode })))}
              </div>
              <OrgShareSelector orgOptions={orgOptions} selectedIds={classCreateForm.sharedOrgIds} onChange={(sharedOrgIds) => setClassCreateForm((prev) => ({ ...prev, sharedOrgIds }))} />
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">고급</div>
                <label className="mt-3 flex items-center gap-3 text-sm font-semibold text-slate-800">
                  <input type="checkbox" checked={classCreateForm.required} onChange={(e) => setClassCreateForm((prev) => ({ ...prev, required: e.target.checked }))} />
                  필수 교육으로 표시
                </label>
              </div>
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <div className="font-semibold text-slate-800">요약</div>
                <div className="mt-2"><span className="font-medium text-slate-700">상위</span> · {selectedCat?.title || '—'}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">클래스명</span> · {classCreateForm.title || '—'}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">대상 플랜</span> · {educationVisibilityPlanLabel(classCreateForm.visibilityPlanCode)}</div>
                <div className="mt-2"><span className="font-medium text-slate-700">공유 조직</span> · {classCreateForm.sharedOrgIds?.length ? `${classCreateForm.sharedOrgIds.length}곳` : '없음'}</div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setClassCreateStep(1)} className={educationButtonClass.outline}>이전</button>
                <button
                  type="button"
                  onClick={() => createStandaloneClassMutation.mutate()}
                  disabled={createStandaloneClassMutation.isPending || !classCreateCategoryId || !classCreateForm.title.trim()}
                  className={educationButtonClass.commit}
                >
                  {createStandaloneClassMutation.isPending ? '저장 중...' : '클래스 저장'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    )
  }

  if (adminSurface === 'lessonAdd') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">강의 추가</h2>
          {selectedClass && <span className="text-sm font-medium text-slate-500">· {catalogClassDisplayTitle(selectedClass)}</span>}
          {lessonAddStep >= 2 && (
            <button type="button" onClick={() => setLessonAddStep(1)} className="text-sm font-semibold text-brand-sky hover:underline">
              카테고리·클래스 변경
            </button>
          )}
        </div>
        <AdminStepStrip labels={lessonAddLabels} currentStep={lessonAddStep} />
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          {lessonTargetIsCategoryBucket && lessonAddStep >= 2 ? (
            <div className="mb-4 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
              별도의 클래스(강의묶음) 없이 강의 생성이 진행됩니다.
            </div>
          ) : null}
          {lessonAddStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">상위 카테고리와 클래스</h3>
                <p className="mt-1 text-sm text-slate-500">강의가 들어갈 상위 그룹을 고른 뒤, 등록할 클래스(또는 클래스 없이 이 그룹에만 속하는 강의)를 선택합니다.</p>
              </div>
              <AdminCategoryPickerGrid
                categories={categories}
                loading={categoriesQuery.isLoading || classesQuery.isLoading}
                selectedId={pickerCategoryId}
                onSelect={(id) => {
                  setPickerCategoryId(id)
                  const cat = categories.find((c) => c.id === id)
                  setSelectedClassId(cat?.directLessonsClassId ?? null)
                }}
              />
              {pickerCategoryId ? (
                <div className="grid max-w-xl gap-2">
                  <label className="text-sm font-medium text-slate-700">등록할 클래스</label>
                  <select
                    value={selectedClassId ?? ''}
                    onChange={(e) => setSelectedClassId(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
                  >
                    {(() => {
                      const cat = categories.find((c) => c.id === pickerCategoryId)
                      const opts = []
                      if (cat?.directLessonsClassId) {
                        opts.push(
                          <option key="direct" value={cat.directLessonsClassId}>
                            이 상위 그룹에만 등록 (클래스 묶음 없음)
                          </option>,
                        )
                      }
                      classes
                        .filter((c) => c.educationCategoryId === pickerCategoryId && !c.lessonBucket)
                        .forEach((c) => {
                          opts.push(
                            <option key={c.id} value={c.id}>
                              {c.title || c.category || `클래스 #${c.id}`}
                            </option>,
                          )
                        })
                      return opts
                    })()}
                  </select>
                  {pickerCategoryId &&
                  !classes.some((c) => c.educationCategoryId === pickerCategoryId && !c.lessonBucket) &&
                  categories.some((c) => c.id === pickerCategoryId && c.directLessonsClassId) ? (
                    <p className="text-xs leading-relaxed text-slate-600">
                      별도의 클래스(강의묶음) 없이 강의 생성이 진행됩니다.
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <input
                      value={newClassTitle}
                      onChange={(e) => setNewClassTitle(e.target.value)}
                      placeholder="새 클래스 이름"
                      className="min-w-[160px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => createSubClassMutation.mutate()}
                      disabled={createSubClassMutation.isPending || !pickerCategoryId || !newClassTitle.trim()}
                      className={educationButtonClass.createSoft}
                    >
                      새 클래스 만들기
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => selectedClassId && setLessonAddStep(2)} disabled={!selectedClassId} className={educationButtonClass.secondary}>
                  다음
                </button>
              </div>
            </div>
          )}
          {lessonAddStep === 2 && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <h3 className="text-lg font-bold text-slate-900">강의 명 · 링크 · 자료</h3>
                <p className="mt-1 text-sm text-slate-500">유튜브 공유 링크(watch, embed, shorts, youtu.be 등)를 입력합니다.</p>
              </div>
              <input value={lessonForm.title} onChange={(e) => setLessonForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="강의명" className="md:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700">유튜브 URL</label>
                <input
                  value={lessonForm.youtubeUrl}
                  onChange={(e) => setLessonForm((prev) => ({ ...prev, youtubeUrl: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=… 또는 youtu.be/…"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none ${
                    lessonForm.youtubeUrl.trim() && !isValidYoutubeUrl(lessonForm.youtubeUrl)
                      ? 'border-rose-300 bg-rose-50/50 focus:border-rose-400'
                      : 'border-slate-200 focus:border-brand-sky'
                  }`}
                />
                {lessonForm.youtubeUrl.trim() && (
                  <p className={`mt-1 text-xs font-medium ${isValidYoutubeUrl(lessonForm.youtubeUrl) ? 'text-emerald-700' : 'text-rose-600'}`}>
                    {isValidYoutubeUrl(lessonForm.youtubeUrl)
                      ? `인식된 영상 ID: ${extractYoutubeVideoIdClient(lessonForm.youtubeUrl)}`
                      : '유효한 유튜브 주소가 아닙니다. watch·embed·shorts·youtu.be 형식 또는 11자 영상 ID를 확인하세요.'}
                  </p>
                )}
              </div>
              <textarea value={lessonForm.description} onChange={(e) => setLessonForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="강의 설명" rows={4} className="md:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                <label className="text-sm font-semibold text-slate-700">강의 자료 (선택)</label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="file"
                    onChange={(e) => uploadLessonMaterial(e.target.files?.[0])}
                    disabled={uploadingLessonMaterial}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-100 disabled:opacity-60"
                  />
                  {lessonForm.materialUrl && (
                    <a href={lessonForm.materialUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">자료 보기</a>
                  )}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {uploadingLessonMaterial ? '업로드 중입니다.' : lessonForm.materialName ? `업로드된 자료: ${lessonForm.materialName}` : 'PDF, 이미지, 문서 파일을 업로드할 수 있습니다.'}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 md:col-span-2">
                <button type="button" onClick={() => setLessonAddStep(1)} className={educationButtonClass.outline}>이전</button>
                <button
                  type="button"
                  onClick={() => {
                    if (!lessonForm.title.trim() || !isValidYoutubeUrl(lessonForm.youtubeUrl)) return
                    setLessonAddOrderDraft(buildLessonAddOrderDraftFromLessons(lessons))
                    setLessonAddStep(3)
                  }}
                  disabled={!lessonForm.title.trim() || !isValidYoutubeUrl(lessonForm.youtubeUrl) || uploadingLessonMaterial}
                  className={educationButtonClass.secondary}
                >
                  다음
                </button>
              </div>
            </div>
          )}
          {lessonAddStep === 3 && (
            <div className="grid max-w-2xl gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">강의 순서</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  이 클래스의 기존 강의와 이번에 추가할 <b>「{lessonForm.title || '신규 강의'}」</b>를 한 목록에서 드래그해 순서를 정합니다. 맨 위가 먼저 재생·노출됩니다.
                </p>
              </div>
              {detailQuery.isLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">강의 목록을 불러오는 중입니다.</div>
              ) : (
                <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-slate-50/50">
                  {lessonAddOrderDraft.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500">순서를 구성할 수 없습니다. 이전 단계로 돌아가 확인하세요.</div>
                  ) : lessonAddOrderDraft.map((entry, idx) => {
                    const key = lessonAddDraftKey(entry)
                    const label = entry.kind === 'new'
                      ? `신규 · ${lessonForm.title || '제목 없음'}`
                      : (lessons.find((l) => l.id === entry.id)?.title || `강의 #${entry.id}`)
                    return (
                      <div
                        key={key}
                        draggable
                        onDragStart={() => setDragLessonAddDraftKey(key)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => moveLessonAddDraftRow(key)}
                        onDragEnd={() => setDragLessonAddDraftKey(null)}
                        className={`flex w-full cursor-grab items-center gap-3 px-4 py-3 text-left text-sm active:cursor-grabbing ${dragLessonAddDraftKey === key ? 'opacity-60' : ''} ${entry.kind === 'new' ? 'bg-teal-50/80' : 'hover:bg-white'}`}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">{idx + 1}</span>
                        <span className="min-w-0 flex-1 font-semibold text-slate-900">{label}</span>
                        <span className="shrink-0 text-slate-400" title="드래그해서 순서 변경">☰</span>
                      </div>
                    )
                  })}
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setLessonAddStep(2)} className={educationButtonClass.outline}>이전</button>
                <button type="button" onClick={() => lessonAddOrderDraft.length > 0 && setLessonAddStep(4)} disabled={lessonAddOrderDraft.length === 0 || detailQuery.isLoading} className={educationButtonClass.secondary}>
                  다음
                </button>
              </div>
            </div>
          )}
          {lessonAddStep === 4 && (
            <div className="grid max-w-md gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">공개 여부 · 등록</h3>
                <p className="mt-1 text-sm text-slate-500">비공개 강의는 수강자 목록에 숨겨질 수 있습니다.</p>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
                <input type="checkbox" checked={lessonForm.publicFlag} onChange={(e) => setLessonForm((prev) => ({ ...prev, publicFlag: e.target.checked }))} />
                강의를 공개합니다
              </label>
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setLessonAddStep(3)} className={educationButtonClass.outline}>이전</button>
                <button type="button" onClick={() => selectedClass && createLessonMutation.mutate()} disabled={!selectedClass || createLessonMutation.isPending || lessonAddOrderDraft.length === 0} className={educationButtonClass.create}>
                  {createLessonMutation.isPending ? '등록 중...' : '강의 등록'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    )
  }

  if (adminSurface === 'lessonManage') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">강의 관리</h2>
          {selectedClass && (
            <button type="button" onClick={() => startLessonAdd(true)} className={educationButtonClass.createSoft}>
              새 강의 등록
            </button>
          )}
        </div>
        <p className="text-sm text-slate-600">카테고리 → 클래스 → 강의 순으로 고른 뒤, 오른쪽에서 강의를 수정하거나 삭제합니다.</p>

        <div className="grid gap-4 lg:grid-cols-3">
          <section className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
              <span className="h-4 w-1 shrink-0 rounded-full bg-brand-sky" />
              <h3 className="text-sm font-bold text-slate-900">카테고리</h3>
            </div>
            <div className="mt-3 min-h-[200px] max-h-[min(52vh,480px)] flex-1 space-y-1 overflow-y-auto pr-1">
              {categoriesQuery.isLoading || classesQuery.isLoading ? (
                <div className="py-6 text-center text-sm text-slate-500">불러오는 중입니다.</div>
              ) : categories.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500">등록된 상위 카테고리가 없습니다.</div>
              ) : (
                categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setPickerCategoryId(cat.id)
                      setSelectedLessonId(null)
                    }}
                    className={`block w-full rounded-xl border px-3 py-2.5 text-left text-sm transition ${pickerCategoryId === cat.id ? educationSelectedClass.item : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}
                  >
                    <span className="block font-semibold text-slate-900">{cat.title || '제목 없음'}</span>
                    <span className="mt-0.5 block text-[11px] text-slate-500">{cat.scope === 'PLATFORM' ? '플랫폼' : cat.orgName || '조직'} · {statusLabel(cat.status)}</span>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
              <span className="h-4 w-1 shrink-0 rounded-full bg-teal-600" />
              <h3 className="text-sm font-bold text-slate-900">클래스</h3>
            </div>
            <div className="mt-3 min-h-[200px] max-h-[min(52vh,480px)] flex-1 space-y-1 overflow-y-auto pr-1">
              {!pickerCategoryId ? (
                <div className="py-6 text-center text-sm text-slate-500">왼쪽에서 카테고리를 선택하세요.</div>
              ) : lessonManageNamedClasses.length === 0 && !lessonManageBucketId ? (
                <div className="py-6 text-center text-sm text-slate-500">이 카테고리에 등록할 클래스가 없습니다.</div>
              ) : lessonManageNamedClasses.length === 0 && lessonManageBucketId ? (
                <div className="py-6 text-center text-sm text-slate-500">생성된 클래스 없음</div>
              ) : (
                <>
                  {lessonManageNamedClasses.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedClassId(c.id)
                        setSelectedLessonId(null)
                      }}
                      className={`block w-full rounded-xl border px-3 py-2.5 text-left text-sm transition ${selectedClassId === c.id ? educationSelectedClass.item : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}
                    >
                      <span className="block font-semibold text-slate-900">{c.title || c.category || `클래스 #${c.id}`}</span>
                    </button>
                  ))}
                  {lessonManageBucketId && lessonManageNamedClasses.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClassId(lessonManageBucketId)
                        setSelectedLessonId(null)
                      }}
                      className={`mt-1 block w-full rounded-xl border px-3 py-2.5 text-left text-sm transition ${selectedClassId === lessonManageBucketId ? educationSelectedClass.item : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}
                    >
                      <span className="block font-semibold text-slate-900">묶음 없이 등록된 강의</span>
                      <span className="mt-0.5 block text-[11px] font-medium text-slate-500">별도 클래스 없이 이 상위 그룹에만 속한 강의</span>
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-1 lg:min-h-[min(56vh,560px)]">
            <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2">
                <span className="h-4 w-1 shrink-0 rounded-full bg-slate-800" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">강의</h3>
                  {selectedClass ? (
                    <p className="mt-0.5 text-xs text-slate-500">{catalogClassDisplayTitle(selectedClass)}</p>
                  ) : (
                    <p className="mt-0.5 text-xs text-slate-500">클래스를 선택하세요</p>
                  )}
                </div>
              </div>
            </div>

            {!selectedClass ? (
              <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-center text-sm text-slate-500">
                가운데 열에서 클래스를 고르면 강의 목록과 편집 폼이 열립니다.
              </div>
            ) : (
              <>
                <div>
                  <div className="mb-2 text-xs font-semibold text-slate-500">목록 · 드래그로 순서 변경</div>
                  <div className="max-h-[200px] divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/50">
                    {detailQuery.isLoading ? (
                      <div className="p-4 text-sm text-slate-500">강의를 불러오는 중입니다.</div>
                    ) : lessons.length === 0 ? (
                      <div className="p-4 text-sm text-slate-500">등록된 강의가 없습니다. 상단 「새 강의 등록」을 이용하세요.</div>
                    ) : (
                      lessons.map((lesson) => (
                        <button
                          key={lesson.id}
                          type="button"
                          draggable
                          onClick={() => selectLesson(lesson.id)}
                          onDragStart={() => setDraggedLessonId(lesson.id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => moveLesson(lesson.id)}
                          onDragEnd={() => setDraggedLessonId(null)}
                          className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-white ${!isCreatingLesson && selectedLesson?.id === lesson.id ? educationSelectedClass.row : ''} ${draggedLessonId === lesson.id ? 'opacity-50' : ''}`}
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">{lesson.sortOrder}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-slate-900">{lesson.title}</span>
                            <span className="mt-0.5 flex flex-wrap gap-1 text-[11px] font-semibold text-slate-500">
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">{lesson.publicFlag ? '공개' : '비공개'}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">조회 {lesson.viewedCount || 0}</span>
                            </span>
                          </span>
                          <span className="shrink-0 text-slate-300">☰</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="min-h-0 flex-1 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-xs font-bold text-brand-navy">강의 편집</div>
                      <h4 className="mt-1 text-base font-bold text-slate-900">{selectedLesson ? selectedLesson.title : '목록에서 강의를 선택하세요'}</h4>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input type="checkbox" checked={lessonForm.publicFlag} onChange={(e) => setLessonForm((prev) => ({ ...prev, publicFlag: e.target.checked }))} />
                      강의 공개
                    </label>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <input value={lessonForm.title} onChange={(e) => setLessonForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="강의명" className="md:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
                    <input value={lessonForm.youtubeUrl} onChange={(e) => setLessonForm((prev) => ({ ...prev, youtubeUrl: e.target.value }))} placeholder="유튜브 링크" className="md:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
                    <textarea value={lessonForm.description} onChange={(e) => setLessonForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="강의 설명" rows={3} className="md:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none" />
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                      <label className="text-sm font-semibold text-slate-700">강의 자료</label>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="file"
                          onChange={(e) => uploadLessonMaterial(e.target.files?.[0])}
                          disabled={uploadingLessonMaterial}
                          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-100 disabled:opacity-60"
                        />
                        {lessonForm.materialUrl && (
                          <a href={lessonForm.materialUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">자료 보기</a>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {uploadingLessonMaterial ? '업로드 중입니다.' : lessonForm.materialName ? `업로드된 자료: ${lessonForm.materialName}` : 'PDF, 이미지, 문서 파일을 업로드할 수 있습니다.'}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-slate-700">강의 순서 (숫자)</label>
                      <input
                        type="number"
                        min={1}
                        value={lessonForm.sortOrder}
                        onChange={(e) => setLessonForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                        className="mt-1 w-full max-w-xs rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none"
                      />
                      <p className="mt-1 text-xs text-slate-500">저장 시 반영됩니다. 드래그 정렬과 함께 쓸 수 있습니다.</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 md:col-span-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedLesson || deleteLessonMutation.isPending) return
                          if (!window.confirm('이 강의를 삭제할까요? 되돌릴 수 없습니다.')) return
                          deleteLessonMutation.mutate(selectedLesson.id)
                        }}
                        disabled={!selectedLesson || deleteLessonMutation.isPending}
                        className={educationButtonClass.danger}
                      >
                        {deleteLessonMutation.isPending ? '삭제 중…' : '삭제'}
                      </button>
                      <button
                        type="button"
                        onClick={() => selectedLesson && updateLessonMutation.mutate()}
                        disabled={!selectedLesson || uploadingLessonMaterial || updateLessonMutation.isPending}
                        className={educationButtonClass.commit}
                      >
                        {updateLessonMutation.isPending ? '저장 중...' : '변경 저장'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        <p className="text-xs text-slate-500 lg:hidden">가로가 좁을 때는 세 열이 세로로 쌓입니다. 스크롤해 카테고리 → 클래스 → 강의 순으로 확인하세요.</p>
      </div>
    )
  }

  if (adminSurface === 'stats' || adminSurface === 'classManage') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={goHub} className={educationButtonClass.outline}>관리 홈으로</button>
          <h2 className="text-lg font-bold text-slate-900">{adminSurface === 'stats' ? '수강 현황' : '클래스 관리'}</h2>
          {adminSurface === 'classManage' && (
            <button type="button" onClick={() => { setAdminSurface('stats'); if (!selectedClassId && manageableClasses[0]?.id) setSelectedClassId(manageableClasses[0].id) }} className="text-sm font-semibold text-slate-600 hover:underline">
              수강 통계 보기
            </button>
          )}
        </div>
        <div className="grid gap-5 2xl:grid-cols-[360px_minmax(0,1fr)]">
          <aside>{classManageSidebar}</aside>
          <main className="space-y-5">
            {adminSurface === 'classManage' && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="text-xs font-bold text-brand-navy">선택한 클래스</div>
                    <h3 className="mt-1 text-xl font-bold text-slate-900">{selectedClass ? categoryName(selectedClass) : '클래스를 선택하세요'}</h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input type="checkbox" checked={classForm.required} onChange={(e) => setClassForm((prev) => ({ ...prev, required: e.target.checked }))} />
                      필수 교육
                    </label>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input type="checkbox" checked={classForm.status === 'PUBLISHED'} onChange={(e) => setClassForm((prev) => ({ ...prev, status: e.target.checked ? 'PUBLISHED' : 'DRAFT' }))} />
                      클래스 공개
                    </label>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {isOperator && (
                    <select value={classForm.scope} onChange={(e) => setClassForm((prev) => ({ ...prev, scope: e.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none md:col-span-2">
                      <option value="ORGANIZATION">조직 클래스</option>
                      <option value="PLATFORM">설계EZ 공통</option>
                    </select>
                  )}
                  <input value={classForm.title} onChange={(e) => setClassForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="클래스명" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none md:col-span-2" />
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700">대상 플랜</label>
                    {educationVisibilityPlanSelect(classForm.visibilityPlanCode, (visibilityPlanCode) => setClassForm((prev) => ({ ...prev, visibilityPlanCode })))}
                  </div>
                  <textarea value={classForm.description} onChange={(e) => setClassForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="짧은 설명" rows={3} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none md:col-span-2" />
                  <textarea value={classForm.intro} onChange={(e) => setClassForm((prev) => ({ ...prev, intro: e.target.value }))} placeholder="상세 소개" rows={3} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none md:col-span-2" />
                  <input value={classForm.notice} onChange={(e) => setClassForm((prev) => ({ ...prev, notice: e.target.value }))} placeholder="공지사항" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none md:col-span-2" />
                  <OrgShareSelector orgOptions={orgOptions} selectedIds={classForm.sharedOrgIds} onChange={(sharedOrgIds) => setClassForm((prev) => ({ ...prev, sharedOrgIds }))} />
                  <div className="flex flex-wrap justify-end gap-2 md:col-span-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedClass || deleteClassMutation.isPending) return
                        if (!window.confirm('이 클래스를 보관(비노출) 처리할까요? 카테고리에 바로 달린 강의용 묶음은 삭제할 수 없습니다.')) return
                        deleteClassMutation.mutate(selectedClass.id)
                      }}
                      disabled={!selectedClass || deleteClassMutation.isPending}
                      className={educationButtonClass.danger}
                    >
                      {deleteClassMutation.isPending ? '처리 중…' : '보관'}
                    </button>
                    <button type="button" onClick={() => selectedClass && updateClassMutation.mutate()} disabled={!selectedClass || updateClassMutation.isPending} className={educationButtonClass.commit}>
                      {updateClassMutation.isPending ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              </section>
            )}
            {adminSurface === 'stats' && (
              <section className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ['수강신청', statsQuery.data?.memberCount || 0],
                    ['수강중', statsQuery.data?.inProgressMemberCount || 0],
                    ['완료', statsQuery.data?.completedMemberCount || 0],
                    ['완료율', `${statsQuery.data?.completionRate || 0}%`],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="text-xs text-slate-500">{label}</div>
                      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-bold text-slate-900">강의 기준 현황</h3>
                  <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200">
                    {(statsQuery.data?.lessons || []).length === 0 ? <div className="p-4 text-sm text-slate-500">집계할 강의가 없습니다.</div> : statsQuery.data.lessons.map((lesson) => (
                      <div key={lesson.lessonId} className="grid gap-2 px-4 py-3 text-sm sm:grid-cols-[1fr_90px_90px_90px]">
                        <div className="font-semibold text-slate-900">{lesson.sortOrder}. {lesson.title}</div>
                        <div>조회 {lesson.viewedCount || 0}</div>
                        <div>완료 {lesson.completedCount || 0}</div>
                        <div className="font-bold text-brand-navy">{lesson.completionRate || 0}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </main>
        </div>
      </div>
    )
  }

  return null
}


export default function EducationCenterPage() {
  const { hasAnyRole } = useAuth()
  const canManage = roleCanManage(hasAnyRole)
  const [activeTab, setActiveTab] = useState('catalog')
  const tabs = canManage
    ? [['catalog', '교육/클래스'], ['my', '나의 강의실'], ['admin', '관리자']]
    : [['catalog', '교육/클래스'], ['my', '나의 강의실']]

  return (
    <PageFrame>
      <PageHeader
        title="교육/클래스"
        titleAccessory={<MenuGuideButton slug="education" title="교육/클래스 가이드" />}
        actions={(
          <div className="flex gap-2 overflow-x-auto rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            {tabs.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setActiveTab(id)} className={`shrink-0 rounded-md px-3 py-1.5 text-sm font-semibold ${activeTab === id ? educationSelectedClass.tab : 'text-slate-600 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      />
      {activeTab === 'catalog' && <EducationCatalog mode="catalog" />}
      {activeTab === 'my' && <EducationCatalog mode="my" />}
      {activeTab === 'admin' && canManage && <AdminEducationView />}
    </PageFrame>
  )
}
